import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Avatar,
  Backdrop,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Snackbar,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import AddIcon from '@mui/icons-material/Add';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import HistoryIcon from '@mui/icons-material/History';
import ImageIcon from '@mui/icons-material/Image';
import DevicesOtherIcon from '@mui/icons-material/DevicesOther';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import PlaylistAddIcon from '@mui/icons-material/PlaylistAdd';
import GetAppIcon from '@mui/icons-material/GetApp';
import VisibilityIcon from '@mui/icons-material/Visibility';
import FilterListIcon from '@mui/icons-material/FilterList';
import * as XLSX from 'xlsx';
import Page from 'src/components/Page';
import AlertScreen from 'src/components/Alert';
import DosyaYukleyici, { DOSYA_TIPLERI, tipEksikVar } from 'src/components/DosyaYukleyici';
import axios from 'src/utils/axios';
import { getUser } from 'src/utils/Common';

const DOSYA_HINT = 'Ekipman fotoğrafı, kalibrasyon belgesi, ekipman üzerindeki etiket, fatura görseli vb. güncel dökümanlar eklenebilir.';
const dosyaTipiLabel = t => (DOSYA_TIPLERI.find(x => x.value === t) || { label: 'Diğer' }).label;

// Ekipman sınıflandırma kılavuzu (statik referans — soldaki panel)
const GUIDE = [
  { ust: 'Ölçüm Ekipmanları', cat: '#3b82f6', gruplar: [
    { grup: 'Dijital manifold', items: [
      { c: '9186000132', n: 'TESTO 550S SMART SET DİJİTAL MANİFOLD' },
      { c: '9197066093', n: 'Testo 510 manometre' } ] },
    { grup: 'Elektrik / Akım / Gerilim / Direnç ölçüm cihazları', items: [
      { c: '9186000114', n: 'Wattmetre' },
      { c: '9186000115', n: 'MULTIMETER (TESTO 760-1)' },
      { c: '9197066089', n: 'Dijital multimetre (TESTO 760-3)' },
      { c: '9197066090', n: 'TESTO 770-1 PENS AMPERMETRE' } ] },
    { grup: 'Inverter Checker', items: [ { c: '9197066095', n: 'Inverter checker' } ] },
    { grup: 'Isı / Sıcaklık / Nem ölçüm cihazları', items: [
      { c: '9186000131', n: 'Testo 174T Sıcaklık Ölçer Datalogger' },
      { c: '9197066091', n: 'TESTO 865 TERMAL KAMERA' },
      { c: '9197066092', n: 'TESTO 830-T1 Infrared Sıcaklık Ölçüm Cihazı' } ] },
    { grup: 'Hız / Debi ölçüm cihazları', items: [] },
    { grup: 'Ses ölçüm cihazları', items: [] } ] },
  { ust: 'Gaz Şarjı / Toplama – Vakum Ekipmanları', cat: '#0ea5a3', gruplar: [
    { grup: 'Vakum ekipmanları', items: [
      { c: '9197066096', n: 'wipcool 2F2R vakum pompası' },
      { c: '9197066754', n: 'Hassas vakum saati — Testo 552i' },
      { c: '9197068072', n: 'Hızlı Vakum Hortum Seti 3/8-1/4' } ] },
    { grup: 'Gaz şarjı ekipmanları', items: [
      { c: '9186000126', n: 'Uzaktan kumandalı dijital gaz terazisi' },
      { c: '9197066753', n: 'Hassas gaz terazi — Selenoidli valf — testo 560i' },
      { c: '9197068071', n: 'Klima Gaz Terazisi Fieldpiece SR47' } ] },
    { grup: 'Gaz geri toplama cihazları', items: [
      { c: '9197066755', n: 'Soğutucu Gaz Geri Toplama Cihazı Fieldpiece MR45' },
      { c: '9197066756', n: 'Soğutucu Akışkan 27 litre çift vanalı gaz toplama tüpü' } ] } ] },
  { ust: 'Kaçak / Kontrol / Analiz Ekipmanları', cat: '#e0891b', gruplar: [
    { grup: 'Gaz kaçak kontrol ekipmanları', items: [
      { c: '9186000125', n: 'Buzdolabı Gaz Kaçak Kontrol cihazı' },
      { c: '9186000133', n: 'SOĞUTUCU GAZ DEDEKTÖRÜ TESTO 316-3' },
      { c: '9186000134', n: 'Doğalgaz ve LPG Kaçak Dedektörü TESTO 316-2' },
      { c: '9197067342', n: 'Hırsız pinçof (buzdolabı ürün gaz kaçağı)' } ] },
    { grup: 'Baca gazı analiz cihazı', items: [
      { c: '9197066094', n: 'Testo baca gazı analiz cihazı 300' } ] } ] },
  { ust: 'Diğer Ekipmanlar', cat: '#9333ea', gruplar: [
    { grup: 'Taşıma ekipmanları', items: [
      { c: '9197069509', n: 'Merdiven inme/çıkma cihazı (Aktaran)' },
      { c: '9197068021', n: 'Buzdolabı Nakliye Örtüsü' },
      { c: '9197068022', n: 'Çamaşır-Bulaşık-Fırın Nakliye Örtüsü' },
      { c: '9197068367', n: 'BUZDOLABI NAKLİYE KAYIŞI' },
      { c: '9197069651', n: 'Çamaşır-Bulaşık-Fırın Nakliye Kayışı' } ] },
    { grup: 'Azot Jeneratörü', items: [
      { c: '9186000127', n: 'Taşınabilir Azot Jeneratörü' },
      { c: '9197066110', n: 'Duvar Tipi AZOT JENERATÖRÜ' } ] },
    { grup: 'Ozon Cihazı', items: [ { c: '9197066109', n: 'Ozon Cihazı' } ] },
    { grup: 'Klima Temizlik Ekipmanları', items: [
      { c: '9197066757', n: 'Duvar Tipi Klima Temizleme Aparatı' },
      { c: '9197066758', n: 'Şarjlı klima temizleme makinesi' } ] },
    { grup: 'Boru Bükme Ekipmanları', items: [
      { c: '9197067509', n: 'Black Diamond Boru Bükme (Ters Bükmeli)' },
      { c: '9197067510', n: 'Silikon Bakır Boru Bükme 1/4 & 5/8' },
      { c: '9197067511', n: 'Silikon Bakır Boru Bükme 3/4' } ] },
    { grup: 'Bandajlama Makinası', items: [ { c: '9197069581', n: 'Bandajlama Makinesi' } ] },
    { grup: 'Diğer Ekipmanlar', items: [] } ] }
];

const styles = {
  root: (theme) => ({ padding: theme.spacing(3) }),
  headerChips: (theme) => ({ display: 'flex', gap: theme.spacing(1), flexWrap: 'wrap', marginTop: theme.spacing(0.5) }),
  toolbar: (theme) => ({ display: 'flex', alignItems: 'center', marginBottom: theme.spacing(2) }),
  spacer: { flexGrow: 1 },
  tableWrap: { overflowX: 'auto' },

  dlgTitle: (theme) => ({ display: 'flex', alignItems: 'center', gap: theme.spacing(1) }),
  dlgSub: (theme) => ({ fontSize: 13, color: theme.palette.text.secondary, marginTop: '2px' }),
  sectionHead: (theme) => ({
    display: 'flex', alignItems: 'center', gap: theme.spacing(1),
    marginTop: theme.spacing(2), marginBottom: theme.spacing(1)
  }),
  stepBadge: (theme) => ({
    width: 24, height: 24, borderRadius: '50%',
    backgroundColor: theme.palette.secondary.main, color: theme.palette.secondary.contrastText,
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    fontSize: 13, fontWeight: 700, flex: '0 0 auto'
  }),
  sectionTitle: { fontWeight: 600 },

  matchedBox: (theme) => ({
    display: 'flex', alignItems: 'center', gap: theme.spacing(1),
    padding: theme.spacing(1), borderRadius: '8px',
    backgroundColor: theme.palette.grey[100], marginTop: theme.spacing(1)
  }),
  catalogCard: (theme) => ({
    borderLeft: `4px solid ${theme.palette.secondary.main}`,
    backgroundColor: theme.palette.grey[50], borderRadius: '8px',
    padding: theme.spacing(1.5), marginTop: theme.spacing(1)
  }),
  newCard: (theme) => ({
    borderLeft: `4px solid #e65100`,
    backgroundColor: theme.palette.grey[50], borderRadius: '8px',
    padding: theme.spacing(1.5), marginTop: theme.spacing(1.5)
  }),
  cardHead: (theme) => ({
    display: 'flex', alignItems: 'center', gap: '6px', marginBottom: theme.spacing(1),
    color: theme.palette.text.secondary, fontSize: 12, fontWeight: 600,
    textTransform: 'uppercase', letterSpacing: 0.4
  }),
  catLabel: (theme) => ({ fontWeight: 600, color: theme.palette.text.secondary, fontSize: 12 }),
  catValue: (theme) => ({ color: theme.palette.text.primary, fontSize: 14 }),
  uzmChip: { margin: '2px' },

  /* renkli seçili ekipman kartı (kompakt) */
  eqCard: (theme) => ({
    borderRadius: '10px',
    padding: theme.spacing(1.25),
    background: 'linear-gradient(135deg, #fff6e6 0%, #ffffff 65%)',
    border: '1px solid #ffe0b2'
  }),
  eqAvatar: (theme) => ({
    backgroundColor: theme.palette.secondary.main,
    color: theme.palette.secondary.contrastText,
    width: 32,
    height: 32
  }),
  eqName: (theme) => ({ fontWeight: 700, fontSize: 15, color: theme.palette.text.primary, lineHeight: 1.2 }),
  eqGroup: (theme) => ({ fontSize: 12, color: theme.palette.text.secondary }),
  uzmChipColored: { margin: '2px', height: 22, backgroundColor: '#ede7f6', color: '#4527a0', fontWeight: 600 },

  /* yeni ekipman tanımı kartı */
  newEqCard: (theme) => ({
    borderRadius: '10px',
    padding: theme.spacing(1.5),
    background: 'linear-gradient(135deg, #fff3e0 0%, #ffffff 62%)',
    border: '1px solid #ffcc80'
  }),
  newEqAvatar: { backgroundColor: '#e65100', color: '#fff', width: 32, height: 32 },
  newEqTitle: { fontWeight: 700, fontSize: 15, color: '#e65100', lineHeight: 1.2 },
  newEqSub: { fontSize: 12, color: '#8d6e63' },
  subLabel: {
    fontSize: 10.5, fontWeight: 700, color: '#9e9e9e',
    textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: '2px'
  },

  /* yeni ekipman - adım adım kutu */
  wizardBox: (theme) => ({
    border: '1px solid #ffcc80',
    borderRadius: '10px',
    padding: theme.spacing(1.5),
    backgroundColor: '#fff'
  }),
  wizardHead: {
    display: 'flex', alignItems: 'center', gap: '6px',
    fontWeight: 700, color: '#e65100', marginBottom: '4px'
  },
  wizardSub: { fontWeight: 400, fontSize: 12, color: '#8d6e63', marginLeft: '4px' },
  greenBanner: {
    display: 'flex', alignItems: 'center', gap: '6px',
    backgroundColor: '#e8f5e9', color: '#2e7d32', fontWeight: 600, fontSize: 13,
    borderRadius: '8px', padding: '6px 12px', marginBottom: '10px'
  },
  infoBanner: {
    display: 'flex', alignItems: 'center', gap: '6px',
    backgroundColor: '#eceff1', color: '#455a64', fontWeight: 500, fontSize: 13,
    borderRadius: '8px', padding: '6px 12px'
  },
  warnBanner: {
    display: 'flex', alignItems: 'center', gap: '6px',
    backgroundColor: '#fff3e0', color: '#e65100', fontWeight: 600, fontSize: 13,
    borderRadius: '8px', padding: '6px 12px'
  },

  /* sol kılavuz paneli */
  guideWrap: (theme) => ({
    borderRadius: '10px', border: `1px solid ${theme.palette.divider}`,
    background: theme.palette.grey[50], padding: theme.spacing(1),
    display: 'flex', flexDirection: 'column', height: '100%'
  }),
  guideHead: (theme) => ({
    display: 'flex', alignItems: 'center', gap: '6px', fontWeight: 700, fontSize: 14,
    color: theme.palette.text.primary, marginBottom: '8px'
  }),
  guideBody: { overflowY: 'auto', maxHeight: 'calc(100vh - 230px)', paddingRight: '4px', marginTop: '10px' },
  guideUst: (theme) => ({
    border: `1px solid ${theme.palette.divider}`, borderRadius: '10px', overflow: 'hidden',
    marginBottom: '10px', background: theme.palette.background.paper
  }),
  guideUstHead: {
    display: 'flex', alignItems: 'center', gap: '8px', padding: '11px 12px',
    borderLeft: '4px solid', cursor: 'pointer', userSelect: 'none', fontWeight: 700, fontSize: 14
  },
  guideDot: { width: 10, height: 10, borderRadius: '50%', flex: '0 0 auto' },
  guideUstName: { flex: 1, lineHeight: 1.25 },
  guideCount: (theme) => ({
    fontSize: 11.5, fontWeight: 700, color: theme.palette.text.secondary,
    fontVariantNumeric: 'tabular-nums', background: theme.palette.action.hover,
    borderRadius: '10px', padding: '1px 8px'
  }),
  guideChevron: (theme) => ({ transition: 'transform .18s ease', color: theme.palette.text.secondary }),
  guideUstBody: { padding: '0 12px 8px' },
  guideGrup: (theme) => ({ padding: '10px 0 5px', borderTop: `1px solid ${theme.palette.divider}` }),
  guideGrupHead: (theme) => ({
    display: 'flex', alignItems: 'center', gap: '8px', fontSize: 13.5, fontWeight: 700,
    color: theme.palette.text.primary, marginBottom: '6px'
  }),
  guideGrupBullet: { width: 7, height: 7, borderRadius: '2px', flex: '0 0 auto' },
  guideItem: (theme) => ({
    display: 'flex', gap: '10px', alignItems: 'baseline', padding: '5px 0 5px 15px',
    fontSize: 13, color: theme.palette.text.primary, lineHeight: 1.4
  }),
  guideCode: (theme) => ({
    fontFamily: 'ui-monospace, Consolas, monospace', fontVariantNumeric: 'tabular-nums',
    fontSize: 12, fontWeight: 700, color: theme.palette.text.primary, background: theme.palette.action.selected,
    padding: '2px 8px', borderRadius: '6px', whiteSpace: 'nowrap', flex: '0 0 auto'
  }),
  guideDiger: (theme) => ({ color: theme.palette.text.secondary, fontStyle: 'italic', fontSize: 12.5, padding: '4px 0 4px 15px' }),

  /* kılavuz — tablo görünümü */
  guideTable: { borderCollapse: 'collapse', width: '100%' },
  gtHeadCell: {
    position: 'sticky', top: 0, zIndex: 2, textAlign: 'left',
    background: '#2b3441', color: '#f4f6f9',
    fontSize: 10, fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase',
    padding: '9px 10px', whiteSpace: 'nowrap'
  },
  gtCell: (theme) => ({ border: `1px solid ${theme.palette.divider}`, padding: '7px 10px', verticalAlign: 'top', fontSize: 12.5 }),
  gtUst: { verticalAlign: 'middle', fontWeight: 700, lineHeight: 1.25 },
  gtGrup: (theme) => ({ fontWeight: 600, color: theme.palette.text.primary, lineHeight: 1.3 }),
  gtAcik: { padding: '3px 8px' },
  gtItem: (theme) => ({ display: 'flex', gap: '8px', alignItems: 'baseline', padding: '4px 0', lineHeight: 1.35, color: theme.palette.text.primary }),
  gtItem2: (theme) => ({ borderTop: `1px dashed ${theme.palette.divider}` }),
  gtItemClickable: (theme) => ({
    cursor: 'pointer', borderRadius: '6px', paddingLeft: '4px', paddingRight: '4px',
    transition: 'background-color .12s',
    '&:hover': { backgroundColor: theme.palette.action.hover }
  }),

  photoZone: (theme) => ({ display: 'flex', gap: theme.spacing(2), alignItems: 'flex-start', flexWrap: 'wrap' }),
  photoFrame: (theme) => ({
    width: 140, height: 140, borderRadius: '8px', border: `1px dashed ${theme.palette.grey[400]}`,
    display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden',
    backgroundColor: theme.palette.grey[50], color: theme.palette.grey[500]
  }),
  photoImg: { maxWidth: '100%', maxHeight: '100%', cursor: 'pointer' },
  statusToggle: (theme) => ({
    display: 'flex', gap: theme.spacing(0.75), alignItems: 'center', flexWrap: 'wrap',
    padding: theme.spacing(0.5, 1),
    border: `1px solid ${theme.palette.divider}`, borderRadius: '999px',
    backgroundColor: theme.palette.background.paper
  }),
  fotoPreview: { maxWidth: '100%', maxHeight: 480, display: 'block', margin: '0 auto' },

  /* liste */
  filterBar: (theme) => ({
    display: 'flex', gap: theme.spacing(1), flexWrap: 'wrap',
    alignItems: 'center', marginBottom: theme.spacing(1.5)
  }),
  searchField: { minWidth: 240, flex: 1 },
  statChip: { fontWeight: 600 },
  statusAlindi: { backgroundColor: '#e8f5e9', color: '#2e7d32', fontWeight: 600 },
  statusAlinmadi: { backgroundColor: '#ffebee', color: '#c62828', fontWeight: 600 },
  warnChip: { backgroundColor: '#fff3e0', color: '#e65100', fontWeight: 600 },
  thumb: { width: 38, height: 38, cursor: 'pointer' },
  thumbEmpty: (theme) => ({ width: 38, height: 38, backgroundColor: theme.palette.grey[200], color: theme.palette.grey[500] }),
  headRow: (theme) => ({ '& th': { backgroundColor: theme.palette.grey[50], fontWeight: 600, whiteSpace: 'nowrap' } }),
  zebra: (theme) => ({ '& tbody tr:nth-of-type(odd)': { backgroundColor: theme.palette.action.hover } }),
  emptyState: (theme) => ({ textAlign: 'center', padding: theme.spacing(6), color: theme.palette.text.secondary })
};

const apiToInput = s => {
  if (!s) return '';
  const p = String(s).split('.');
  return p.length === 3 ? `${p[2]}-${p[1]}-${p[0]}` : '';
};
const inputToApi = s => {
  if (!s) return '';
  const p = String(s).split('-');
  return p.length === 3 ? `${p[2]}.${p[1]}.${p[0]}` : '';
};

// Karşılaştırma için sadeleştir: küçük harf + Türkçe karakter sadeleştir + harf/rakam dışını at.
// "soğutucu"≈"sogutucu", "Testo 656"≈"testo656" eşleşir.
const normalizeText = s =>
  String(s || '')
    .toLocaleLowerCase('tr-TR')
    .replace(/ğ/g, 'g').replace(/ü/g, 'u').replace(/ş/g, 's')
    .replace(/ı/g, 'i').replace(/ö/g, 'o').replace(/ç/g, 'c')
    .replace(/[^a-z0-9]/g, '');

const emptyForm = {
  HardwareID: 0,
  StokNo: '',
  UstGrupId: '',
  GrupId: '',
  HardwareType: '',
  HardwareNumber: '',
  GivenCount: 1,
  GivenDate: '',
  TakenDate: '',
  SerialNo: '',
  CalibrationDate: '',
  CalibrationDuration: '',
  CalibrationStatu: 0,
  Description: '',
  TeknisyenKodu: '',
  FileData: '',
  ExistingFoto: ''
};

const emptyTanim = {
  UstGrupId: '',
  GrupId: '',
  Description: '',
  Marka: '',
  Model: '',
  KalibrasyonIhtiyaci: false,
  KalibrasyonGun: '',
  UzmanlikIds: []   // opsiyonel uzmanlıklar
};

function EkipmanYonetimi() {
  const user = getUser() || {};

  const topServiceShopId = Number(user.TopServiceShopCode) || 0;
  const serviceShopId = Number(user.ServiceShopId) || 0;
  const bolgeAdi = user.TopServiceShopName || String(topServiceShopId || '');
  const servisAdi =
    user.ServiceShopName ||
    (user.ServiceShopCode ? String(user.ServiceShopCode) : String(serviceShopId || ''));

  // Admin ise bölge/servis seçebilir; değilse kendi bölge/servisi (backend de zorlar)
  const [isAdmin, setIsAdmin] = useState(false);   // 37 (ada göre) / 8: tüm bölge-servis combosu, EKLEME YOK
  const [canAdd, setCanAdd] = useState(false);     // 9: kendi servisi + Yeni/Toplu Ekle açık
  const [isByYonetici, setIsByYonetici] = useState(false); // 8 (ve 37 değil): chip "Bölge Yöneticisi görünümü"
  const [bolgeList, setBolgeList] = useState([]);   // {BirimKodu, Dsc}
  const [servisList, setServisList] = useState([]); // {BirimKodu, Dsc}
  const [selTop, setSelTop] = useState(topServiceShopId);     // aktif bölge (üst birim)
  const [selService, setSelService] = useState(serviceShopId); // aktif servis (0 = bölgedeki tümü)

  const [loading, setLoading] = useState(false);
  const [list, setList] = useState([]);

  const [ustGruplar, setUstGruplar] = useState([]);
  const [gruplar, setGruplar] = useState([]);
  const [cinsler, setCinsler] = useState([]);
  const [ekipmanUzmanliklar, setEkipmanUzmanliklar] = useState([]);
  const [uzmanliklar, setUzmanliklar] = useState([]); // uzmanlık ana listesi (T_YAPI_UZMANLIK)
  const [teknisyenler, setTeknisyenler] = useState([]); // servise bağlı teknisyenler
  const [fotoluEkipmanlar, setFotoluEkipmanlar] = useState(() => new Set()); // katalogda fotoğrafı olan ekipman ID'leri
  const [yeniDosyalar, setYeniDosyalar] = useState([]);       // bu oturumda eklenen belgeler
  const [mevcutDosyalar, setMevcutDosyalar] = useState([]);   // kayıttaki mevcut belgeler
  const [silinenDosyaIds, setSilinenDosyaIds] = useState([]); // silinecek mevcut belge id'leri
  const [kapakYeniUid, setKapakYeniUid] = useState(null);     // yeni dosyalardan kapak seçimi

  const [snackOpen, setSnackOpen] = useState(false);
  const [snackType, setSnackType] = useState('success');
  const [snackMessage, setSnackMessage] = useState('');
  const notify = (type, message) => {
    setSnackType(type);
    setSnackMessage(message);
    setSnackOpen(true);
  };

  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [tanim, setTanim] = useState(emptyTanim);
  const [matchState, setMatchState] = useState('idle'); // idle | matched | new
  const [stokInput, setStokInput] = useState(''); // autocomplete input metni
  const [picking, setPicking] = useState(false); // arama kutusu açık mı
  const [tanimStep, setTanimStep] = useState(0); // yeni ekipman sihirbaz adımı
  const [justDefined, setJustDefined] = useState(false); // yeni tanım sonrası ipucu
  const [guideSearch, setGuideSearch] = useState(''); // sol kılavuz arama
  const [expandedGuide, setExpandedGuide] = useState({}); // kılavuz üst grup aç/kapa
  const guideBodyRef = useRef(null); // kılavuz gövdesi (auto-scroll için)
  // kılavuzda üst grupların kullanıcı tarafından sürükle-bırakla belirlenen sırası (kalıcı)
  const UST_SIRA_KEY = 'EKIPMAN_KILAVUZ_UST_SIRA';
  const [ustSira, setUstSira] = useState(() => {
    try { const v = JSON.parse(localStorage.getItem(UST_SIRA_KEY)); return Array.isArray(v) ? v : []; }
    catch { return []; }
  });
  const dragUstId = useRef(null); // sürüklenmekte olan üst grup id'si
  const [dragOverUstId, setDragOverUstId] = useState(null); // üzerine gelinen üst grup (görsel ipucu)
  // üst grup sırası değiştiğinde kalıcı yap (boş sırada yazma — kayıtlıyı ezmesin)
  useEffect(() => {
    if (!ustSira || ustSira.length === 0) return;
    try { localStorage.setItem(UST_SIRA_KEY, JSON.stringify(ustSira)); } catch (e) { /* yok say */ }
  }, [ustSira]);
  const [confirmState, setConfirmState] = useState({ open: false, id: 0, name: '' });
  const [logOpen, setLogOpen] = useState(false);
  const [logRows, setLogRows] = useState([]);
  const [fotoOpen, setFotoOpen] = useState(false);
  const [fotoData, setFotoData] = useState('');
  const [galeri, setGaleri] = useState({ open: false, baslik: '', loading: false, items: [] });

  // toplu ekle (üstte tek giriş → alta liste)
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkList, setBulkList] = useState([]);   // eklenen (onaylanmış) satırlar
  const [bulkDraft, setBulkDraft] = useState(null); // üstteki giriş taslağı
  const [bulkResult, setBulkResult] = useState(null);

  // liste filtre + sayfalama
  const [search, setSearch] = useState('');
  const [filterUst, setFilterUst] = useState('');
  const [filterGrup, setFilterGrup] = useState('');
  const [filterStatu, setFilterStatu] = useState('all'); // all | 1 | 0
  const [filterTeknisyen, setFilterTeknisyen] = useState(''); // teknisyen kodu
  const [filterUzmanlik, setFilterUzmanlik] = useState('');   // uzmanlık adı
  const [onlyUnmatched, setOnlyUnmatched] = useState(false);
  const [onlyExpired, setOnlyExpired] = useState(false); // kalibrasyon süresi dolanlar
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const cinsById = useMemo(() => {
    const m = new Map();
    cinsler.forEach(c => m.set(c.Id, c));
    return m;
  }, [cinsler]);
  const ustById = useMemo(() => {
    const m = new Map();
    ustGruplar.forEach(u => m.set(u.Id, u));
    return m;
  }, [ustGruplar]);
  const grupById = useMemo(() => {
    const m = new Map();
    gruplar.forEach(g => m.set(g.Id, g));
    return m;
  }, [gruplar]);

  const cinsUzmanliklari = cinsId => ekipmanUzmanliklar.filter(eu => eu.EkipmanId === cinsId);
  const grupPathByIds = (ustId, grupId) => {
    const u = ustById.get(ustId);
    const g = grupById.get(grupId);
    return [u && u.Aciklama, g && g.Aciklama].filter(Boolean).join(' / ');
  };
  const grupPath = cinsId => {
    const c = cinsById.get(cinsId);
    if (!c) return '';
    return grupPathByIds(c.UstGrupId, c.GrupId);
  };
  // virgüllü uzmanlık kodlarını isim listesine çevir
  const uzmanlikKodToNames = kodlar => {
    if (!kodlar) return [];
    return String(kodlar).split(',').map(s => Number(s.trim())).filter(Boolean)
      .map(k => { const u = uzmanliklar.find(x => x.Kod === k); return u ? u.Aciklama : null; })
      .filter(Boolean);
  };
  // yeni tanımdaki seçilen grup yolu (etiketlerde göstermek için)
  const tanimGrupPath = () => {
    const u = ustById.get(tanim.UstGrupId);
    const g = grupById.get(tanim.GrupId);
    return [u && u.Aciklama, g && g.Aciklama].filter(Boolean).join(' / ');
  };

  useEffect(() => {
    document.title = 'Ekipman Yönetimi';
    fetchAll();
    // Yetki: admin ise bölge/servis combolarını göster + bölge listesini çek
    axios.get('/GetEkipmanYonetimYetki')
      .then(res => {
        const y = res.data || {};
        // 3 rol: IsAdmin (37 ada göre / 8 BY Yönetici) = combo + Tümü/Tümü, EKLEME YOK.
        //        CanAdd (9 Servis Çalışan Yönetimi) = kendi servisi + ekleme açık.
        // Çakışma kuralı: admin baskın -> admin ekleyemez (CanAdd yalnız admin DEĞİLken geçerli).
        const admin = !!y.IsAdmin;
        setIsAdmin(admin);
        setCanAdd(!admin && !!y.CanAdd);
        // Chip etiketi: sadece BY Yönetici (8) ise "Bölge Yöneticisi görünümü"; Ekipman Admin (37) öncelikli -> "Yönetici görünümü"
        setIsByYonetici(!!y.IsByYonetici && !y.IsEkipmanAdmin);
        if (admin) {
          // Admin: combo seçimi yapılmadan tüm bölgeler listelensin, combolar Tümü/Tümü
          setSelTop(0);
          setSelService(0);
          loadList(0, 0);
          axios.get(`/GetBolgeAdiKodu/${user.TopServiceShopCode}`)
            .then(r => setBolgeList(Array.isArray(r.data) ? r.data : []))
            .catch(() => setBolgeList([]));
        }
        // admin değilse: servis görünümü — kendi servisi (fetchAll yükledi)
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Admin bölge seçince o bölgenin servislerini yükle
  const bolgeSec = bolge => {
    const top = bolge && bolge.BirimKodu ? bolge.BirimKodu : 0; // 0 = tüm bölgeler
    setSelTop(top);
    setSelService(0);          // varsayılan: (bölgedeki) tüm servisler
    setServisList([]);
    loadList(top, 0);
    if (top) {                 // belirli bir bölge seçildiyse servislerini yükle (yetki filtresiz Apollo SP'si)
      axios.get(`/GetEkipmanBolgeServisler/${top}`)
        .then(r => setServisList(Array.isArray(r.data) ? r.data : []))
        .catch(() => setServisList([]));
    }
  };
  // Admin servis seçince
  const servisSec = servis => {
    const svc = servis ? servis.BirimKodu : 0;
    setSelService(svc);
    loadList(selTop, svc);
  };

  // Aktif servis değişince o servisin teknisyenlerini çek (ekleme dialogu için)
  useEffect(() => {
    const svc = selService || serviceShopId;
    if (!svc) { setTeknisyenler([]); return; }
    axios.get(`/GetServisTeknisyenler/${svc}`)
      .then(res => setTeknisyenler(Array.isArray(res.data) ? res.data : []))
      .catch(() => setTeknisyenler([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selService]);

  // Diyalog açıkken tarayıcı GERİ tuşu: sayfadan çıkmak yerine popup'ı kapatıp listede kal
  useEffect(() => {
    if (!dialogOpen) return;
    window.history.pushState({ ekipmanDialog: true }, '');
    const onPop = () => setDialogOpen(false);
    window.addEventListener('popstate', onPop);
    return () => {
      window.removeEventListener('popstate', onPop);
      // Diyalog Vazgeç/Kaydet ile kapandıysa eklediğimiz history girişini temizle
      // (geri tuşuyla kapandıysa zaten pop edilmiştir, tekrar geri gitmeyelim)
      if (window.history.state && window.history.state.ekipmanDialog) {
        window.history.back();
      }
    };
  }, [dialogOpen]);

  // Kılavuz araması değişince ilk eşleşmeye otomatik kaydır (yoksa başa dön)
  useEffect(() => {
    const c = guideBodyRef.current;
    if (!c) return;
    if (!guideSearch.trim()) { c.scrollTop = 0; return; }
    const t = setTimeout(() => {
      const el = c.querySelector('[data-guide-hit="1"]');
      if (el && el.scrollIntoView) el.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }, 60);
    return () => clearTimeout(t);
  }, [guideSearch]);

  // Yeni girişte grup seçilince kılavuzda o satıra kaydır
  useEffect(() => {
    if (matchState !== 'new' || !tanim.GrupId) return;
    const c = guideBodyRef.current;
    if (!c) return;
    const t = setTimeout(() => {
      const el = c.querySelector('[data-guide-sel="1"]');
      if (el && el.scrollIntoView) el.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }, 90);
    return () => clearTimeout(t);
  }, [tanim.GrupId, matchState]);

  const loadList = (top, service) =>
    axios
      .get(`/GetHardwareList/${top}/${service}`)
      .then(res => setList(Array.isArray(res.data) ? res.data : []));
  const fetchList = () => loadList(selTop, selService);

  const fetchCinsler = () =>
    axios.get('/GetEkipmanlar').then(res => {
      const arr = Array.isArray(res.data) ? res.data : [];
      setCinsler(arr);
      return arr;
    });

  const fetchAll = () => {
    if (!serviceShopId) {
      notify('warning', 'Kullanıcınız bir servise bağlı değil, ekipman listesi getirilemez.');
      return;
    }
    setLoading(true);
    Promise.all([
      fetchList(),
      axios.get('/GetEkipmanUstGruplar'),
      axios.get('/GetEkipmanGruplar'),
      fetchCinsler(),
      axios.get('/GetEkipmanUzmanliklar'),
      axios.get(`/GetServisTeknisyenler/${serviceShopId}`),
      axios.get('/GetUzmanliklar'),
      axios.get('/GetEkipmanFotoIdleri')
    ])
      .then(([, ustRes, grupRes, , ekUzmRes, tekRes, uzmRes, fotoRes]) => {
        setUstGruplar(Array.isArray(ustRes.data) ? ustRes.data : []);
        setGruplar(Array.isArray(grupRes.data) ? grupRes.data : []);
        setEkipmanUzmanliklar(Array.isArray(ekUzmRes.data) ? ekUzmRes.data : []);
        setTeknisyenler(Array.isArray(tekRes.data) ? tekRes.data : []);
        setUzmanliklar(Array.isArray(uzmRes.data) ? uzmRes.data : []);
        setFotoluEkipmanlar(new Set(Array.isArray(fotoRes.data) ? fotoRes.data : []));
      })
      .catch(() => notify('error', 'Veriler yüklenirken bir hata oluştu.'))
      .finally(() => setLoading(false));
  };

  const ustGruplarActive = ustGruplar.filter(u => u.Active);
  const tanimGruplarForUst = gruplar.filter(g => g.Active && g.UstGrupId === tanim.UstGrupId);
  // serviste tanımlı (katalog dışı) ekipman: seçili değer sentetik olarak tanim'dan üretilir
  const serviceNew = matchState === 'servicenew';
  const selectedCins = form.HardwareType
    ? cinsById.get(form.HardwareType)
    : (serviceNew
        ? {
            Id: 0, Description: tanim.Description, Active: true,
            UstGrupId: tanim.UstGrupId, GrupId: tanim.GrupId,
            Marka: tanim.Marka, Model: tanim.Model, StokNo: '',
            KalibrasyonIhtiyaci: !!tanim.KalibrasyonIhtiyaci, KalibrasyonGun: tanim.KalibrasyonGun
          }
        : null);
  // aktif ama gruba bağlı değilse önce bağlanmalı
  const needsBind = !!(selectedCins && selectedCins.Active && (!selectedCins.UstGrupId || !selectedCins.GrupId));
  // cihaz bilgileri adımı hazır mı? (seçili + bağlanması gereken durumda değil)
  const deviceReady = (matchState === 'matched' || serviceNew) && !!selectedCins && !needsBind;
  // admin (katalog) tanımına göre kalibrasyon zorunlu mu? gerekli değilse alanlar pasif
  const kalibGerekli = !!(selectedCins && selectedCins.KalibrasyonIhtiyaci);
  // düz formda (yeni ekipman) kalibrasyon zorunluluğu doğrudan tanim'dan gelir
  const kalibGerekliEff = matchState === 'new' ? !!tanim.KalibrasyonIhtiyaci : kalibGerekli;

  // yeni ekipman adı/stok no yazılırken eşleşen tanımlı (aktif) ekipmanlar
  // Türkçe karakter + boşluk/noktalama farklarını yok sayar (soğutucu≈sogutucu, testo656≈testo 656)
  const qAdNorm = normalizeText(tanim.Description);
  const qStokNorm = normalizeText(form.StokNo);
  const benzerEkipmanlar = (() => {
    if (qAdNorm.length < 2 && qStokNorm.length < 2) return [];
    const out = [];
    for (const c of cinsler) {
      if (!c.Active || !c.GrupId) continue;
      const adHit = qAdNorm.length >= 2 && normalizeText(c.Description).includes(qAdNorm);
      const stokHit = qStokNorm.length >= 2 && normalizeText(c.StokNo).includes(qStokNorm);
      if (adHit || stokHit) {
        out.push(c);
        if (out.length >= 6) break;
      }
    }
    return out;
  })();

  const setField = (name, value) => setForm(f => ({ ...f, [name]: value }));
  const setTanimField = (name, value) => setTanim(t => ({ ...t, [name]: value }));

  const openNew = () => {
    // Yalnız "Servis Çalışan Yönetimi" (canAdd) ekleyebilir; admin görünümü (37/8) ekleyemez
    if (!canAdd) return;
    setForm({ ...emptyForm });
    setTanim({ ...emptyTanim });
    setMatchState('idle');
    setStokInput('');
    setPicking(true);
    setJustDefined(false);
    setGuideSearch('');
    setYeniDosyalar([]);
    setMevcutDosyalar([]);
    setSilinenDosyaIds([]);
    setKapakYeniUid(null);
    setDialogOpen(true);
  };

  const openEdit = row => {
    const isServiceNew = !row.HardwareType; // katalog ID yok => serviste tanımlı
    const c = isServiceNew ? null : cinsById.get(row.HardwareType);
    setForm({
      HardwareID: row.HardwareID,
      StokNo: c ? c.StokNo || '' : '',
      UstGrupId: isServiceNew ? (row.UstGrupId || '') : (c ? c.UstGrupId : ''),
      GrupId: isServiceNew ? (row.GrupId || '') : (c ? c.GrupId : ''),
      HardwareType: row.HardwareType || '',
      HardwareNumber: row.HardwareNumber || '',
      GivenCount: row.GivenCount || 0,
      GivenDate: apiToInput(row.GivenDate),
      TakenDate: apiToInput(row.TakenDate),
      SerialNo: row.SerialNo || '',
      CalibrationDate: apiToInput(row.CalibrationDate),
      CalibrationDuration: row.CalibrationDuration || '',
      CalibrationStatu: row.CalibrationStatu || 0,
      Description: row.Description || '',
      TeknisyenKodu: row.TeknisyenKodu || '',
      FileData: '',
      ExistingFoto: row.FileData || ''
    });
    if (isServiceNew) {
      // serviste tanımlı kayıt — tanim'e doldur, servicenew moduna geç
      setTanim({
        UstGrupId: row.UstGrupId || '',
        GrupId: row.GrupId || '',
        Description: row.HardwareName || row.HardwareTypeDescription || '',
        Marka: row.Marka || '',
        Model: row.Model || '',
        KalibrasyonIhtiyaci: !!row.KalibrasyonIhtiyaci,
        KalibrasyonGun: row.CalibrationDuration || '',
        UzmanlikIds: row.UzmanlikKodlari
          ? String(row.UzmanlikKodlari).split(',').map(s => Number(s.trim())).filter(Boolean)
          : []
      });
      setMatchState('servicenew');
      setStokInput('');
      setPicking(false);
    } else {
      setTanim({ ...emptyTanim });
      setMatchState('matched');
      setStokInput(c ? c.Description || '' : '');
      setPicking(!!(c && !c.Active)); // pasifse arama açık gelsin
    }
    setJustDefined(false);
    // çoklu belgeler: bu oturum eklenenleri sıfırla, kayıttaki belgeleri çek
    setYeniDosyalar([]);
    setSilinenDosyaIds([]);
    setMevcutDosyalar([]);
    setKapakYeniUid(null);
    if (row.HardwareID) {
      axios.get(`/GetServisEkipmanDosyalar/${row.HardwareID}`)
        .then(res => setMevcutDosyalar(Array.isArray(res.data) ? res.data : []))
        .catch(() => setMevcutDosyalar([]));
    }
    setDialogOpen(true);
  };

  // Belgeyi büyük göster/aç (yeni: base64 elde; mevcut: sunucudan çek)
  const gosterDosya = (kaynak, item) => {
    if (kaynak === 'yeni') {
      if (/^image\//i.test(item.contentType || '')) openFoto(item.base64);
      else if (item.url) window.open(item.url, '_blank');
      return;
    }
    axios.get(`/GetServisEkipmanDosya/${item.Id}`).then(res => {
      const d = res.data || {};
      if (/^image\//i.test(d.ContentType || '')) openFoto(d.FileData);
      else if (d.FileData) window.open(`data:${d.ContentType || 'application/octet-stream'};base64,${d.FileData}`, '_blank');
      else notify('info', 'Belge getirilemedi.');
    }).catch(() => notify('error', 'Belge getirilirken hata oluştu.'));
  };

  const silMevcutDosya = id => {
    setSilinenDosyaIds(prev => prev.includes(id) ? prev : [...prev, id]);
    setMevcutDosyalar(prev => prev.filter(m => m.Id !== id));
  };

  // Kapak (ana görsel) seç
  const onKapakSec = (kaynak, item) => {
    if (kaynak === 'yeni') {
      setKapakYeniUid(item.uid);
      setMevcutDosyalar(prev => prev.map(m => ({ ...m, Kapak: false })));
      return;
    }
    // mevcut (kayıtlı) belge — hemen sunucuda kapak yap
    setKapakYeniUid(null);
    if (!form.HardwareID) return;
    axios.get(`/SetServisEkipmanKapak/${form.HardwareID}/${item.Id}`)
      .then(() => {
        setMevcutDosyalar(prev => prev.map(m => ({ ...m, Kapak: m.Id === item.Id })));
        fetchList(); // liste küçük görseli güncellensin
        notify('success', 'Ana görsel güncellendi.');
      })
      .catch(() => notify('error', 'Kapak güncellenemedi.'));
  };

  // Autocomplete: stok no / ekipman adına göre filtre + "yeni tanımla" seçeneği
  const filterEkipmanOptions = (options, state) => {
    const q = normalizeText(state.inputValue);
    let out = options;
    if (q) {
      out = options.filter(
        o =>
          normalizeText(o.StokNo).includes(q) ||
          normalizeText(o.Description).includes(q)
      );
    }
    // "yeni ekipman girişi" seçeneği her zaman en üstte
    return [{ __new: true, input: state.inputValue.trim() }, ...out];
  };

  // Autocomplete seçim: mevcut ekipman ya da "yeni tanımla"
  const handlePick = val => {
    if (!val) {
      resetMatch();
      setStokInput('');
      return;
    }
    if (val.__new) {
      const t = String(val.input || '').trim();
      const isStok = /^[0-9]+$/.test(t.replace(/\s/g, '')); // sayıysa stok no, değilse ad
      setForm(f => ({ ...f, StokNo: isStok ? t : '', HardwareType: '', UstGrupId: '', GrupId: '' }));
      setTanim({ ...emptyTanim, Description: isStok ? '' : t });
      setMatchState('new');
      setStokInput(t);
      setPicking(false);
      setTanimStep(0);
      return;
    }
    setForm(f => ({
      ...f,
      HardwareType: val.Id,
      StokNo: val.StokNo || '',
      UstGrupId: val.UstGrupId,
      GrupId: val.GrupId,
      CalibrationDuration: val.KalibrasyonGun || '', // katalog periyodu (düzenlenebilir)
      // katalogdaki açıklamayı cihaz bilgileri Açıklama alanına doldur; açıklaması yoksa temizle
      // (eski ekipmanın açıklaması yeni seçimde üzerinde kalmasın)
      Description: val.Aciklama ? String(val.Aciklama) : ''
    }));
    setTanim({ ...emptyTanim }); // grup bağlama selectleri için sıfırla
    setMatchState('matched');
    setStokInput(val.Description || '');
    setPicking(!val.Active); // pasif seçilirse arama açık kalsın
  };

  const startPicking = () => setPicking(true);

  const resetMatch = () => {
    setForm(f => ({ ...f, HardwareType: '', UstGrupId: '', GrupId: '' }));
    setTanim({ ...emptyTanim });
    setMatchState('idle');
    setStokInput('');   // aramaya dönerken combobox'ı boşalt
    setPicking(true);
    setTanimStep(0);
    setJustDefined(false);
  };

  // Aynı ad ya da stok no ile tanımlı aktif ekipman var mı? (duplike engeli)
  const findDuplicateEkipman = () => {
    const nameKey = tanim.Description.trim().toLocaleLowerCase('tr-TR');
    const stokKey = String(form.StokNo || '').trim().toLocaleLowerCase('tr-TR');
    const byName = nameKey && cinsler.find(c => c.Active && String(c.Description || '').trim().toLocaleLowerCase('tr-TR') === nameKey);
    if (byName) return { cins: byName, reason: 'ad' };
    const byStok = stokKey && cinsler.find(c => c.Active && String(c.StokNo || '').trim().toLocaleLowerCase('tr-TR') === stokKey);
    if (byStok) return { cins: byStok, reason: 'stok no' };
    return null;
  };

  // Serviste tanımlı (katalog dışı) yeni ekipman: KATALOG satırı OLUŞTURMAZ.
  // Girilen sınıflandırmayı taşıyıp cihaz bilgileri adımına geçer; kayıt SetHardware ile yapılır.
  // Aktif ama grupsuz ekipmanı gruba bağla (katalogda güncelle) — uzmanlıkları korur
  const bindGroup = () => {
    if (!selectedCins) return;
    if (!tanim.UstGrupId || !tanim.GrupId) return notify('error', 'Üst grup ve grup seçilmelidir.');
    const c = selectedCins;
    const existingUzm = ekipmanUzmanliklar
      .filter(eu => eu.EkipmanId === c.Id)
      .map(eu => eu.UzmanlikId);

    const payload = {
      Id: c.Id,
      UstGrupId: tanim.UstGrupId,
      GrupId: tanim.GrupId,
      Description: c.Description,
      StokNo: c.StokNo || '',
      Aciklama: c.Aciklama || '',
      Marka: c.Marka || '',
      Model: c.Model || '',
      KalibrasyonIhtiyaci: !!c.KalibrasyonIhtiyaci,
      KalibrasyonGun: c.KalibrasyonIhtiyaci ? c.KalibrasyonGun || null : null,
      Active: true,
      UzmanlikIds: existingUzm
    };

    const ug = tanim.UstGrupId;
    const gg = tanim.GrupId;
    setLoading(true);
    axios
      .post('/SetEkipman', payload)
      .then(res => {
        const r = res.data || {};
        if (r.ErrorCode === '0' || r.ErrorCode === 0) {
          return fetchCinsler().then(() => {
            setForm(f => ({ ...f, UstGrupId: ug, GrupId: gg }));
            setPicking(false);
            notify('success', 'Ekipman gruba bağlandı.');
          });
        }
        notify('error', r.ErrorDescription || 'Gruba bağlama başarısız.');
        return null;
      })
      .catch(() => notify('error', 'Gruba bağlama sırasında hata oluştu.'))
      .finally(() => setLoading(false));
  };

  const onFileSelect = e => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = ev => setField('FileData', btoa(ev.target.result.toString()));
    reader.readAsBinaryString(file);
  };
  const previewFoto = form.FileData || form.ExistingFoto;

  const save = () => {
    // 'new' (düz form) = serviste tanımlı yeni ekipman; 'servicenew' = onu düzenleme
    const isNewFlat = matchState === 'new';
    const isSvcNew = serviceNew || isNewFlat;

    if (isSvcNew) {
      if (!tanim.Description.trim())
        return notify('error', 'Ekipman adı boş olamaz.');
      if (!tanim.UstGrupId || !tanim.GrupId)
        return notify('error', 'Üst grup ve grup seçilmelidir.');
      if (isNewFlat) {
        const dup = findDuplicateEkipman();
        if (dup)
          return notify('error', `Bu ${dup.reason} ile katalogda tanımlı ekipman var: "${dup.cins.Description}". Aramadan mevcut olanı seçin.`);
      }
    } else if (!form.HardwareType) {
      return notify('error', 'Stok no girip ekipmanı eşleştirin (veya yeni ekipman girin).');
    }

    // Kalibrasyon durumu "Alındı" ise son kalibrasyon tarihi zorunlu; "Yok" ise değil
    if (kalibGerekliEff && Number(form.CalibrationStatu) === 1 && !form.CalibrationDate)
      return notify('error', 'Kalibrasyon durumu "Alındı" seçili — son kalibrasyon tarihini girin.');

    // Eklenen her belge için dosya tipi seçimi zorunlu
    if (tipEksikVar(yeniDosyalar))
      return notify('warning', 'Lütfen dosya türünü seçiniz.');

    // Yeni ekipman kaydında en az bir fotoğraf zorunlu
    if (form.HardwareID === 0) {
      const isImg = ct => /^image\//i.test(String(ct || ''));
      const fotoVar =
        yeniDosyalar.some(d => d.tip === 'FOTO' || isImg(d.contentType)) ||
        mevcutDosyalar.some(m => (m.Tip === 'FOTO' || isImg(m.ContentType)) && !silinenDosyaIds.includes(m.Id));
      if (!fotoVar)
        return notify('warning', 'Yeni ekipman için en az bir fotoğraf eklemeniz gerekiyor.');
    }

    const cins = isSvcNew ? null : cinsById.get(form.HardwareType);
    const payload = {
      HardwareID: form.HardwareID,
      TopServiceShopId: selTop || topServiceShopId,
      ServiceShopId: selService || serviceShopId,
      HardwareName: isSvcNew ? tanim.Description : (cins ? cins.Description : ''),
      HardwareNumber: form.HardwareNumber,
      HardwareType: isSvcNew ? 0 : Number(form.HardwareType),
      GivenCount: Number(form.GivenCount) || 0,
      GivenDate: inputToApi(form.GivenDate),
      TakenDate: inputToApi(form.TakenDate),
      SerialNo: form.SerialNo,
      // kalibrasyon gerekmiyorsa ya da durum "Yok" ise tarih boş gönder
      CalibrationDate: (kalibGerekliEff && Number(form.CalibrationStatu) === 1) ? inputToApi(form.CalibrationDate) : '',
      CalibrationDuration: kalibGerekliEff ? (Number(form.CalibrationDuration) || 0) : 0,
      CalibrationStatu: kalibGerekliEff ? (Number(form.CalibrationStatu) || 0) : 0,
      Description: form.Description,
      TeknisyenKodu: form.TeknisyenKodu ? Number(form.TeknisyenKodu) : null,
      // serviste tanımlı (katalog dışı) ekipman alanları — sadece serviste tanımlıda dolu
      UstGrupId: isSvcNew ? (tanim.UstGrupId || null) : null,
      GrupId: isSvcNew ? (tanim.GrupId || null) : null,
      Marka: isSvcNew ? (tanim.Marka || null) : null,
      Model: isSvcNew ? (tanim.Model || null) : null,
      KalibrasyonIhtiyaci: isSvcNew ? !!tanim.KalibrasyonIhtiyaci : null,
      UzmanlikIds: isSvcNew && Array.isArray(tanim.UzmanlikIds) ? tanim.UzmanlikIds : [],
      // FOTOGRAF'ı kapak SP'si yönetiyor; burada yazma
      FileData: '',
      // Çoklu belgeler (kapak: seçili yeni; yoksa mevcutta kapak yoksa ilk yeni foto)
      Dosyalar: (() => {
        const mevcutKapakVar = mevcutDosyalar.some(m => m.Kapak && !silinenDosyaIds.includes(m.Id));
        let kUid = kapakYeniUid;
        if (!kUid && !mevcutKapakVar) {
          const ilk = yeniDosyalar.find(d => d.tip === 'FOTO') || yeniDosyalar.find(d => /^image\//i.test(d.contentType || ''));
          if (ilk) kUid = ilk.uid;
        }
        return yeniDosyalar.map(d => ({
          DosyaAdi: d.ad, ContentType: d.contentType, Tip: d.tip, Kapak: d.uid === kUid, FileData: d.base64
        }));
      })(),
      SilinenDosyaIds: silinenDosyaIds
    };

    setLoading(true);
    axios
      .post('/SetHardware', payload)
      .then(res => {
        const r = res.data || {};
        if (r.ErrorCode === '0' || r.ErrorCode === 0) {
          notify('success', r.ErrorDescription || 'Kaydedildi.');
          setDialogOpen(false);
          return fetchList();
        }
        notify('error', r.ErrorDescription || 'Kayıt başarısız.');
        return null;
      })
      .catch(() => notify('error', 'Kayıt sırasında hata oluştu.'))
      .finally(() => setLoading(false));
  };

  const doDelete = () => {
    const id = confirmState.id;
    setConfirmState({ open: false, id: 0, name: '' });
    setLoading(true);
    axios
      .get(`/DeleteHardware/${id}`)
      .then(res => {
        const r = res.data || {};
        if (r.ErrorCode === '0' || r.ErrorCode === 0) {
          notify('success', r.ErrorDescription || 'Silindi.');
          return fetchList();
        }
        notify('error', r.ErrorDescription || 'Silme başarısız.');
        return null;
      })
      .catch(() => notify('error', 'Silme sırasında hata oluştu.'))
      .finally(() => setLoading(false));
  };

  const openLogs = row => {
    setLogRows([]);
    setLoading(true);
    axios
      .get(`/GetHardwareLogs/${row.HardwareID}`)
      .then(res => {
        setLogRows(Array.isArray(res.data) ? res.data : []);
        setLogOpen(true);
      })
      .catch(() => notify('error', 'Log getirilemedi.'))
      .finally(() => setLoading(false));
  };

  const openFoto = base64 => {
    if (!base64) return notify('info', 'Bu donanıma ait dosya yok.');
    setFotoData(base64);
    setFotoOpen(true);
  };

  // Listede foto/karta basınca: o ekipmanın TÜM belgelerini galeri olarak getir
  const openGaleri = row => {
    const c = cinsById.get(row.HardwareType);
    const baslik = (c && c.Description) || row.HardwareTypeDescription || 'Ekipman';
    setGaleri({ open: true, baslik, loading: true, items: [] });
    axios.get(`/GetServisEkipmanDosyalar/${row.HardwareID}`)
      .then(async res => {
        const list = Array.isArray(res.data) ? res.data : [];
        // her belge için önizleme verisini çek (küçük görsel + açma)
        const withData = await Promise.all(list.map(async m => {
          try {
            const r = await axios.get(`/GetServisEkipmanDosya/${m.Id}`);
            const d = r.data || {};
            const ct = d.ContentType || m.ContentType || '';
            return { ...m, ContentType: ct, base64: d.FileData || null,
              dataUrl: d.FileData ? `data:${ct || 'image/jpeg'};base64,${d.FileData}` : null };
          } catch (e) { return { ...m, base64: null, dataUrl: null }; }
        }));
        // hiç belge yoksa, listedeki eski tek FOTOGRAF'ı da göster
        if (withData.length === 0 && row.FileData) {
          withData.push({ Id: 0, DosyaAdi: 'Fotoğraf', Tip: 'FOTO', ContentType: 'image/*',
            base64: row.FileData, dataUrl: 'data:image/*;base64,' + row.FileData });
        }
        setGaleri({ open: true, baslik, loading: false, items: withData });
      })
      .catch(() => setGaleri({ open: true, baslik, loading: false, items: [] }));
  };

  // Katalog ekipmanının fotoğrafını (T_YAPI_EKIPMAN_DOSYA) getirip aç
  const openCatalogFoto = ekipmanId => {
    if (!ekipmanId) return;
    setLoading(true);
    axios
      .get(`/GetEkipmanDosyalar/${ekipmanId}`)
      .then(res => {
        const list0 = Array.isArray(res.data) ? res.data : [];
        if (list0.length === 0) { notify('info', 'Bu ekipmana ait fotoğraf bulunamadı.'); return null; }
        return axios.get(`/GetEkipmanDosya/${list0[0].Id}`).then(r => {
          const data = r.data && r.data.FileData;
          if (data) openFoto(data);
          else notify('info', 'Fotoğraf getirilemedi.');
        });
      })
      .catch(() => notify('error', 'Fotoğraf getirilirken hata oluştu.'))
      .finally(() => setLoading(false));
  };
  // ---- Toplu Ekle (satır satır, ekran içi) ----
  const bulkNewRow = () => ({
    uid: `${Date.now()}_${Math.round(performance.now())}_${Math.random().toString(36).slice(2, 7)}`,
    cins: null, isNew: false, input: '',
    Description: '', UstGrupId: '', GrupId: '', Marka: '', Model: '',
    KalibrasyonIhtiyaci: false, KalibrasyonGun: '', UzmanlikIds: [],
    SerialNo: '', GivenCount: 1, TeknisyenKodu: '',
    CalibrationStatu: 0, CalibrationDate: '', Aciklama: '', files: []
  });

  const openBulk = () => {
    if (!canAdd) return; // yalnız Servis Çalışan Yönetimi (9) toplu ekleyebilir
    setBulkResult(null);
    setBulkList([]);
    setBulkDraft(bulkNewRow());
    setBulkOpen(true);
  };
  const bulkRemoveRow = uid => setBulkList(prev => prev.filter(r => r.uid !== uid));
  // listedeki bir satırın bilgilerini composer'a kopyala (Seri No + Açıklama hariç) — aynı ekipmanı farklı seri no ile hızlı eklemek için
  const bulkCloneToDraft = it => setBulkDraft({
    ...it,
    uid: `${Date.now()}_${Math.round(performance.now())}_${Math.random().toString(36).slice(2, 7)}`,
    SerialNo: '',
    Aciklama: '',
    files: (it.files || []).map(f => ({ ...f }))
  });
  const draftPatch = patch => setBulkDraft(d => ({ ...d, ...patch }));
  const draftPick = val => {
    if (!val) return draftPatch({ cins: null, isNew: false, Description: '' });
    if (val.__new) {
      const t = String(val.input || '').trim();
      return draftPatch({ cins: null, isNew: true, Description: t, UstGrupId: '', GrupId: '' });
    }
    draftPatch({ cins: val, isNew: false, Description: '' });
  };

  // satır kalibrasyon gerektiriyor mu? (katalog: tanımdan, yeni: seçimden)
  const bulkRowKalib = r => (r.isNew ? !!r.KalibrasyonIhtiyaci : !!(r.cins && r.cins.KalibrasyonIhtiyaci));
  // satır dolduruldu mu?
  const bulkRowDolu = r => !!(r && (r.cins || r.isNew || (r.input && r.input.trim())));
  const bulkIsImg = ct => /^image\//i.test(String(ct || ''));
  // satır hatası (null = geçerli) — tek-ekleme ile aynı kurallar
  const bulkRowHata = r => {
    if (!bulkRowDolu(r)) return 'Ekipman seçin';
    if (!r.cins && !r.isNew) return 'Ekipman seçilmedi';
    if (r.isNew) {
      if (!r.Description.trim()) return 'Ekipman adı gerekli';
      if (!r.UstGrupId || !r.GrupId) return 'Üst grup ve grup gerekli';
    }
    // kalibrasyon durumu "Alındı" ise tarih zorunlu; "Yok" ise değil
    if (bulkRowKalib(r) && Number(r.CalibrationStatu) === 1 && !r.CalibrationDate)
      return 'Kalibrasyon "Alındı" — son kalibrasyon tarihi gerekli';
    // her belgenin türü seçili olmalı
    if (tipEksikVar(r.files)) return 'Belge türünü seçin';
    // en az bir fotoğraf zorunlu
    if (!(r.files || []).some(d => d.tip === 'FOTO' || bulkIsImg(d.contentType)))
      return 'En az bir fotoğraf ekleyin';
    return null;
  };

  const draftReady = !!bulkDraft && !bulkRowHata(bulkDraft);
  const bulkCatalogCount = bulkList.filter(r => r.cins).length;
  const bulkNewCount = bulkList.filter(r => r.isNew).length;

  // satır kimliği (katalog Id ya da yeni-ad) + seri no — çift kayıt tespiti için
  const bulkRowKimlik = r => (r.isNew ? 'yeni:' + normalizeText(r.Description) : 'kat:' + (r.cins ? r.cins.Id : ''));
  const bulkRowSeri = r => normalizeText(r.SerialNo);

  // taslağı listeye ekle (Ekle / Enter)
  const bulkAddDraft = () => {
    if (!bulkDraft) return;
    const h = bulkRowHata(bulkDraft);
    if (h) return notify('warning', h);
    const seri = bulkRowSeri(bulkDraft);
    if (seri) {
      // dolu seri no listede zaten var mı? (aynı seri no ile ekletme)
      if (bulkList.some(r => bulkRowSeri(r) === seri))
        return notify('warning', `Bu seri no (${bulkDraft.SerialNo}) listede zaten var.`);
    } else {
      // seri no boş: aynı ekipman seri no'suz zaten eklendiyse uyar
      if (bulkList.some(r => bulkRowKimlik(r) === bulkRowKimlik(bulkDraft) && !bulkRowSeri(r)))
        return notify('warning', 'Bu ekipman seri no olmadan listede zaten var. Aynı üründen eklemek için farklı seri no girin.');
    }
    setBulkList(prev => [...prev, {
      ...bulkDraft,
      uid: `${Date.now()}_${Math.round(performance.now())}_${Math.random().toString(36).slice(2, 7)}`
    }]);
    setBulkDraft(bulkNewRow());
  };

  const doBulkSave = () => {
    const svc = selService || serviceShopId;
    const top = selTop || topServiceShopId;
    if (!svc) return notify('warning', 'Toplu ekleme için bağlı bir servis gerekli.');
    if (bulkList.length === 0) return notify('warning', 'Önce en az bir ekipman ekleyin.');

    const payloads = bulkList.map(r => {
      const kalib = bulkRowKalib(r);
      return {
        HardwareID: 0,
        TopServiceShopId: top,
        ServiceShopId: svc,
        HardwareName: r.isNew ? r.Description.trim() : r.cins.Description,
        HardwareNumber: '',
        HardwareType: r.isNew ? 0 : Number(r.cins.Id),
        GivenCount: Number(r.GivenCount) || 0,
        GivenDate: '',
        TakenDate: '',
        SerialNo: r.SerialNo || '',
        CalibrationDate: (kalib && Number(r.CalibrationStatu) === 1) ? inputToApi(r.CalibrationDate) : '',
        CalibrationDuration: kalib ? (r.isNew ? (Number(r.KalibrasyonGun) || 0) : (Number(r.cins.KalibrasyonGun) || 0)) : 0,
        CalibrationStatu: kalib ? (Number(r.CalibrationStatu) || 0) : 0,
        Description: r.Aciklama || '',
        TeknisyenKodu: r.TeknisyenKodu ? Number(r.TeknisyenKodu) : null,
        UstGrupId: r.isNew ? (r.UstGrupId || null) : null,
        GrupId: r.isNew ? (r.GrupId || null) : null,
        Marka: r.isNew ? (r.Marka || null) : null,
        Model: r.isNew ? (r.Model || null) : null,
        KalibrasyonIhtiyaci: r.isNew ? !!r.KalibrasyonIhtiyaci : null,
        UzmanlikIds: r.isNew && Array.isArray(r.UzmanlikIds) ? r.UzmanlikIds : [],
        Dosyalar: (() => {
          const fs = r.files || [];
          const kUid = (fs.find(d => d.tip === 'FOTO') || fs.find(d => bulkIsImg(d.contentType)) || {}).uid;
          return fs.map(d => ({ DosyaAdi: d.ad, ContentType: d.contentType, Tip: d.tip, Kapak: d.uid === kUid, FileData: d.base64 }));
        })(),
        SilinenDosyaIds: []
      };
    });

    setLoading(true);
    axios
      .post('/SetHardwareBulk', payloads)
      .then(res => {
        const r = res.data || {};
        setBulkResult(r);
        notify(r.Failed ? 'warning' : 'success',
          `${r.Inserted || 0} ekipman eklendi${r.Failed ? `, ${r.Failed} hata` : ''}.`);
        if (!r.Failed) setBulkOpen(false);
        return fetchList();
      })
      .catch(() => notify('error', 'Toplu ekleme sırasında hata oluştu.'))
      .finally(() => setLoading(false));
  };

  // ---- liste filtre/sayfalama türetmeleri ----
  // satır kalibrasyon gerektiriyor mu? (katalog: tanımdan, serviste tanımlı: satırdan)
  const rowKalibGerekli = row => {
    const c = cinsById.get(row.HardwareType);
    return row.HardwareType ? !!(c && c.KalibrasyonIhtiyaci) : !!row.KalibrasyonIhtiyaci;
  };
  const alindiCount = list.filter(r => r.CalibrationStatu === 1).length;
  // "Alınmadı" = kalibrasyon GEREKEN ama alınmamış (gerekmeyenler sayılmaz)
  const alinmadiCount = list.filter(r => rowKalibGerekli(r) && r.CalibrationStatu === 0).length;
  const gruplarForFilter = gruplar.filter(g => g.Active && (!filterUst || g.UstGrupId === filterUst));

  // eşleşme sorunu: tanım yok / pasif / grupsuz (serviste tanımlı kayıtlar için sorun yok)
  const matchIssue = row => {
    if (!row.HardwareType) return null; // serviste tanımlı — katalog eşleşmesi aranmaz
    const c = cinsById.get(row.HardwareType);
    if (!c) return 'Tanım yok';
    if (!c.Active) return 'Pasif tanım';
    if (!c.GrupId) return 'Grupsuz';
    return null;
  };
  const unmatchedCount = list.filter(r => matchIssue(r)).length;

  // kalibrasyon süresi dolmuş mu? (sadece tarihi geçmişler; hiç kalibre edilmemişler HARİÇ)
  const kalibExpired = row => {
    if (!row.CalibrationDate) return false;            // tarih yok -> dahil değil
    const gun = Number(row.CalibrationDuration) || 0;
    if (gun <= 0) return false;                        // periyot yok -> süre hesaplanamaz
    const p = String(row.CalibrationDate).split('.');  // "dd.mm.yyyy"
    if (p.length !== 3) return false;
    const d = Number(p[0]), m = Number(p[1]), y = Number(p[2]);
    const bitis = new Date(y, m - 1, d + gun);         // son kalibrasyon + periyot gün
    if (isNaN(bitis.getTime())) return false;
    const bugun = new Date(); bugun.setHours(0, 0, 0, 0);
    return bitis < bugun;
  };
  const expiredCount = list.filter(kalibExpired).length;

  // satırın etkin üst grup/grup id'si (katalog ya da serviste tanımlı)
  const rowUstId = (row, c) => (c ? c.UstGrupId : row.UstGrupId);
  const rowGrupId = (row, c) => (c ? c.GrupId : row.GrupId);

  // bir satırın uzmanlık adları (katalog: cinsUzmanliklari, serviste tanımlı: UzmanlikKodlari)
  const rowUzmanliklar = row => (
    !row.HardwareType
      ? uzmanlikKodToNames(row.UzmanlikKodlari
          ? String(row.UzmanlikKodlari).split(',').map(s => Number(s.trim())).filter(Boolean)
          : [])
      : cinsUzmanliklari(row.HardwareType).map(u => u.Aciklama)
  );

  // teknisyen combo: servise bağlı teknisyenler; yoksa listedeki atanmışlardan türet
  const teknisyenOptions = (teknisyenler.length
    ? teknisyenler.map(t => ({ Kod: t.Id, Ad: t.AdSoyad || String(t.Id) }))
    : Array.from(new Map(list.filter(r => r.TeknisyenKodu)
        .map(r => [r.TeknisyenKodu, { Kod: r.TeknisyenKodu, Ad: r.TeknisyenAdi || String(r.TeknisyenKodu) }])).values())
  ).sort((a, b) => String(a.Ad).localeCompare(String(b.Ad), 'tr'));
  // uzmanlık combo: master uzmanlık listesi; yoksa listedekilerden türet
  const uzmanlikOptions = Array.from(new Set(
    (uzmanliklar.length ? uzmanliklar.map(u => u.Aciklama) : list.flatMap(rowUzmanliklar))
  )).filter(Boolean).sort((a, b) => String(a).localeCompare(String(b), 'tr'));

  const filtered = list.filter(row => {
    const c = cinsById.get(row.HardwareType);
    if (onlyUnmatched && !matchIssue(row)) return false;
    if (onlyExpired && !kalibExpired(row)) return false;
    if (filterUst && rowUstId(row, c) !== filterUst) return false;
    if (filterGrup && rowGrupId(row, c) !== filterGrup) return false;
    if (filterStatu === 1 && row.CalibrationStatu !== 1) return false;
    if (filterStatu === 0 && !(rowKalibGerekli(row) && row.CalibrationStatu === 0)) return false;
    if (filterTeknisyen && String(row.TeknisyenKodu || '') !== String(filterTeknisyen)) return false;
    if (filterUzmanlik && !rowUzmanliklar(row).includes(filterUzmanlik)) return false;
    if (search) {
      const q = search.toLowerCase();
      const hay = [
        c && c.Description, row.HardwareTypeDescription, row.HardwareNumber,
        row.SerialNo, c ? c.Marka : row.Marka, c ? c.Model : row.Model, c && c.StokNo,
        c ? grupPath(row.HardwareType) : grupPathByIds(row.UstGrupId, row.GrupId)
      ].filter(Boolean).join(' ').toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });
  const paged = filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  // Seçili tek servisin "KUL_BIRIM_KODU - açıklama" gösterimi (kod önde, açıklama arkada)
  const servisEtiket = (() => {
    if (isAdmin && !selService) return ''; // Tümü — tek servis yok
    const r = list.find(x => x.ServiceShop || x.ServiceShopName);
    if (r) {
      const kod = (r.ServiceShop != null ? String(r.ServiceShop) : '').trim();
      let ad = String(r.ServiceShopName || '').trim();
      if (kod) {
        // ServiceShopName "kod - açıklama" ya da "açıklama - kod" olabilir; kodu ayıkla, saf açıklama kalsın
        ad = ad.replace(new RegExp('^' + kod + '\\s*-\\s*'), '')
               .replace(new RegExp('\\s*-\\s*' + kod + '$'), '')
               .trim();
      }
      if (kod && ad) return `${kod} - ${ad}`;
      return kod || ad;
    }
    const kod2 = user.ServiceShopCode || selService || '';
    return (kod2 ? kod2 + ' - ' : '') + (servisAdi || '');
  })();

  // Listeyi Excel'e aktar — mevcut filtrelenmiş görünümü (filtre yoksa tümünü) indirir
  const exportExcel = () => {
    if (filtered.length === 0) return notify('warning', 'Dışa aktarılacak kayıt yok.');
    const bolgeAd = kod => {
      const b = bolgeList.find(x => Number(x.BirimKodu) === Number(kod));
      if (b) return b.Dsc;
      return bolgeAdi || (kod != null ? String(kod) : '');
    };
    const rows = filtered.map(row => {
      const c = cinsById.get(row.HardwareType);
      const cA = c && c.Active ? c : null;
      const isSvc = !row.HardwareType;
      const kalib = rowKalibGerekli(row);
      return {
        'Bölge': bolgeAd(row.TopServiceShop),
        'Servis': row.ServiceShopName || servisAdi || '',
        'Servis Kodu': row.ServiceShop != null ? row.ServiceShop : '',
        'Ekipman': (c && c.Description) || row.HardwareTypeDescription || '',
        'Grup': isSvc ? grupPathByIds(row.UstGrupId, row.GrupId) : grupPath(row.HardwareType),
        'Stok No': cA ? (cA.StokNo || '') : '',
        'Seri No': row.SerialNo || '',
        'Marka / Model': (isSvc ? [row.Marka, row.Model] : cA ? [cA.Marka, cA.Model] : []).filter(Boolean).join(' / '),
        'Adet': row.GivenCount || 0,
        'Uzmanlık': rowUzmanliklar(row).join(', '),
        'Teknisyen': row.TeknisyenAdi || '',
        'Son Kalibrasyon': kalib ? (row.CalibrationDate || '') : '',
        'Kalibrasyon Durumu': kalib ? (row.CalibrationStatu === 1 ? 'Var' : 'Yok') : 'Gerekli değil',
        'Kalibrasyon Süresi (gün)': kalib ? (row.CalibrationDuration || '') : '',
        'Açıklama': row.Description || ''
      };
    });
    const ws = XLSX.utils.json_to_sheet(rows);
    ws['!cols'] = [{ wch: 22 }, { wch: 30 }, { wch: 12 }, { wch: 26 }, { wch: 26 }, { wch: 14 }, { wch: 14 }, { wch: 18 }, { wch: 7 }, { wch: 20 }, { wch: 18 }, { wch: 14 }, { wch: 16 }, { wch: 10 }, { wch: 28 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Ekipmanlar');
    const g = new Date();
    const ts = `${g.getFullYear()}${String(g.getMonth() + 1).padStart(2, '0')}${String(g.getDate()).padStart(2, '0')}`;
    XLSX.writeFile(wb, `Ekipman_Listesi_${ts}.xlsx`);
    notify('success', `${rows.length} kayıt Excel'e aktarıldı.`);
  };

  // renkli bilgi hapı (kompakt)
  const pill = (label, value, bg, fg) => (
    <div style={{ background: bg, color: fg, borderRadius: 8, padding: '3px 8px', minWidth: 70 }}>
      <div style={{ fontSize: 10, fontWeight: 600, opacity: 0.85, lineHeight: 1.3 }}>{label}</div>
      <div style={{ fontSize: 12.5, fontWeight: 700, lineHeight: 1.3 }}>{value || '-'}</div>
    </div>
  );

  // sol kılavuz paneli (statik referans, salt-okunur)
  const renderGuide = () => {
    const gq = guideSearch.trim().toLocaleLowerCase('tr-TR');
    const mk = text => {
      if (!gq) return text;
      const i = text.toLocaleLowerCase('tr-TR').indexOf(gq);
      if (i < 0) return text;
      const s = String(text);
      const j = s.toLocaleLowerCase('tr-TR').indexOf(gq);
      if (j < 0) return s;
      return (
        <>{s.slice(0, j)}<mark style={{ background: 'rgba(37,99,235,.32)', borderRadius: 3, padding: '0 1px' }}>{s.slice(j, j + gq.length)}</mark>{s.slice(j + gq.length)}</>
      );
    };
    const cats = ['#3b82f6', '#0ea5a3', '#e0891b', '#9333ea', '#0d9488', '#c2410c', '#7c3aed', '#0891b2'];
    const inNew = matchState === 'new';
    // "Diğer ..." grupları her zaman en alta
    const isDiger = s => /di[ğg]er/i.test(String(s || ''));
    const digerSort = key => (a, b) => (isDiger(a[key]) ? 1 : 0) - (isDiger(b[key]) ? 1 : 0);
    const trCmp = (a, b) => String(a || '').localeCompare(String(b || ''), 'tr');
    // kullanıcının sürükle-bırakla belirlediği sıra önce; sırada olmayanlar alfabetik; "Diğer" hep en altta
    const siraIdx = id => { const i = ustSira.indexOf(id); return i < 0 ? Number.MAX_SAFE_INTEGER : i; };
    const usts = ustGruplar.filter(u => u.Active).slice().sort((a, b) => {
      const da = isDiger(a.Aciklama) ? 1 : 0, db = isDiger(b.Aciklama) ? 1 : 0;
      if (da !== db) return da - db;
      const ia = siraIdx(a.Id), ib = siraIdx(b.Id);
      if (ia !== ib) return ia - ib;
      return trCmp(a.Aciklama, b.Aciklama);
    });
    // sürüklenen üst grubu, bırakılan üst grubun yerine taşı; yeni sırayı kalıcı yap
    const reorderUst = (dropId) => {
      const dragId = dragUstId.current;
      dragUstId.current = null;
      setDragOverUstId(null);
      if (!dragId || dragId === dropId) return;
      const order = usts.map(u => u.Id);
      const from = order.indexOf(dragId);
      const to = order.indexOf(dropId);
      if (from < 0 || to < 0) return;
      order.splice(from, 1);
      order.splice(to, 0, dragId);
      setUstSira(order);
      try { localStorage.setItem(UST_SIRA_KEY, JSON.stringify(order)); } catch (e) { /* yok say */ }
    };
    return (
      <Box sx={styles.guideWrap}>
        <Box sx={styles.guideHead}>
          <MenuBookIcon fontSize="small" /> Ekipman Kılavuzu
          <span style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 8, fontWeight: 400, fontSize: 11 }}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, color: '#2e7d32' }}>
              <CheckCircleIcon style={{ fontSize: 13 }} /> Kalibrasyon
            </span>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, color: '#607d8b' }}>
              <PhotoCameraIcon style={{ fontSize: 13 }} /> Fotoğraf var
            </span>
          </span>
        </Box>
        <TextField
          size="small" fullWidth variant="outlined" placeholder="Kılavuzda ara: kod / ad…"
          value={guideSearch} onChange={e => setGuideSearch(e.target.value)}
          InputProps={{
            startAdornment: (<InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>),
            endAdornment: guideSearch ? (
              <InputAdornment position="end">
                <Tooltip title="Aramayı temizle">
                  <IconButton size="small" edge="end" onClick={() => setGuideSearch('')}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </InputAdornment>
            ) : null
          }}
        />
        <Box sx={styles.guideBody} ref={guideBodyRef}>
          <Box component="table" sx={styles.guideTable}>
            <thead>
              <tr>
                <Box component="th" sx={styles.gtHeadCell}>Üst Grup</Box>
                <Box component="th" sx={styles.gtHeadCell}>Grup</Box>
                <Box component="th" sx={styles.gtHeadCell}>Açıklama</Box>
              </tr>
            </thead>
            <tbody>
              {usts.map((u, ui) => {
                const cat = cats[ui % cats.length];
                const selUst = inNew && tanim.UstGrupId === u.Id;
                let rows = gruplar
                  .filter(g => g.Active && g.UstGrupId === u.Id)
                  .slice().sort(digerSort('Aciklama'))
                  .map(g => {
                    const items = cinsler
                      .filter(c => c.Active && c.GrupId === g.Id)
                      .filter(c => !gq || (String(c.StokNo || '') + ' ' + String(c.Description || '')).toLocaleLowerCase('tr-TR').indexOf(gq) >= 0);
                    return { g, items };
                  });
                if (gq) rows = rows.filter(r => r.items.length > 0);
                if (rows.length === 0) return null;
                return rows.map((r, ri) => {
                  const selGrup = inNew && tanim.GrupId === r.g.Id;
                  // üst gruplar arasında kalın ayırıcı: her üst grubun ilk satırının üstü (üst grubun kendi rengi)
                  const topSep = ui > 0 && ri === 0 ? `3px solid ${cat}` : undefined;
                  return (
                    <tr key={u.Id + '|' + r.g.Id}
                      data-guide-sel={selGrup ? '1' : undefined}
                      style={selGrup ? { background: `color-mix(in srgb, ${cat} 14%, transparent)` } : undefined}>
                      {ri === 0 && (
                        <Box component="td" sx={[styles.gtCell, styles.gtUst]} rowSpan={rows.length}
                          draggable={!gq}
                          onDragStart={e => {
                            if (gq) return;
                            dragUstId.current = u.Id;
                            try { e.dataTransfer.effectAllowed = 'move'; e.dataTransfer.setData('text/plain', String(u.Id)); } catch (err) { /* yok say */ }
                          }}
                          onDragEnd={() => { dragUstId.current = null; setDragOverUstId(null); }}
                          onDragEnter={e => { if (!gq && dragUstId.current && dragUstId.current !== u.Id) e.preventDefault(); }}
                          onDragOver={e => { if (!gq && dragUstId.current && dragUstId.current !== u.Id) { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; if (dragOverUstId !== u.Id) setDragOverUstId(u.Id); } }}
                          onDragLeave={() => { if (dragOverUstId === u.Id) setDragOverUstId(null); }}
                          onDrop={e => { e.preventDefault(); reorderUst(u.Id); }}
                          title={gq ? undefined : 'Sıralamak için sürükleyip bırakın'}
                          style={{ borderLeft: `4px solid ${cat}`, borderTop: dragOverUstId === u.Id ? `3px dashed ${cat}` : topSep, background: `color-mix(in srgb, ${cat} ${dragOverUstId === u.Id ? 22 : (selUst ? 16 : 8)}%, transparent)`, color: cat, fontWeight: selUst ? 800 : 700, cursor: gq ? 'default' : 'grab' }}>
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                            {!gq && <DragIndicatorIcon style={{ fontSize: 14, opacity: 0.55 }} />}
                            {u.Aciklama}
                          </span>
                        </Box>
                      )}
                      <Box component="td" sx={[styles.gtCell, styles.gtGrup]} style={{ ...(selGrup ? { fontWeight: 800 } : {}), borderTop: topSep }}>{r.g.Aciklama}</Box>
                      <Box component="td" sx={[styles.gtCell, styles.gtAcik]} style={{ borderTop: topSep }}>
                        {r.items.length === 0 && !selGrup && <Box component="span" sx={styles.guideDiger}>—</Box>}
                        {r.items.map((c, ii) => {
                          const isHit = gq && (String(c.StokNo || '') + ' ' + String(c.Description || '')).toLocaleLowerCase('tr-TR').indexOf(gq) >= 0;
                          return (
                            <Box key={c.Id} data-guide-hit={isHit ? '1' : undefined}
                              onClick={() => handlePick(c)}
                              title="Bu ekipmanı seç ve devam et"
                              sx={[styles.gtItem, ii > 0 && styles.gtItem2, styles.gtItemClickable]}
                              style={gq && !isHit ? { opacity: 0.3 } : undefined}>
                              {c.StokNo ? <Box component="span" sx={styles.guideCode}>{mk(c.StokNo)}</Box> : null}
                              <span>{mk(c.Description)}</span>
                              {c.KalibrasyonIhtiyaci && (
                                <Tooltip title={`Kalibrasyon gerektirir${c.KalibrasyonGun ? ` · ${c.KalibrasyonGun} gün` : ''}`}>
                                  <CheckCircleIcon style={{ fontSize: 14, color: '#2e7d32', marginLeft: 2, verticalAlign: 'middle' }} />
                                </Tooltip>
                              )}
                              {fotoluEkipmanlar.has(c.Id) && (
                                <Tooltip title="Fotoğrafı görüntüle">
                                  <PhotoCameraIcon
                                    onClick={e => { e.stopPropagation(); openCatalogFoto(c.Id); }}
                                    style={{ fontSize: 14, color: cat, marginLeft: 3, verticalAlign: 'middle', cursor: 'pointer' }} />
                                </Tooltip>
                              )}
                            </Box>
                          );
                        })}
                        {selGrup && (
                          <Box sx={[styles.gtItem, r.items.length && styles.gtItem2]} style={{ color: cat, fontWeight: 700 }}>
                            <Box component="span" sx={styles.guideCode} style={{ color: '#fff', background: cat }}>SEÇİLİ</Box>
                            <span>{(tanim.Description || form.StokNo || 'yeni ekipman') + ' — bu grup altında sınıflanacak'}</span>
                          </Box>
                        )}
                      </Box>
                    </tr>
                  );
                });
              })}
            </tbody>
          </Box>
        </Box>
      </Box>
    );
  };

  return (
    <Page sx={styles.root} title="Ekipman Yönetimi">
      <Backdrop open={loading} style={{ zIndex: 2000, color: '#fff' }}>
        <CircularProgress color="inherit" />
      </Backdrop>

      <Snackbar
        open={snackOpen}
        autoHideDuration={4000}
        onClose={() => setSnackOpen(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <AlertScreen variant={snackType} message={snackMessage} onClose={() => setSnackOpen(false)} />
      </Snackbar>

      <Card sx={{ '&:hover': { transform: 'none', boxShadow: 1 } }}>
        <CardHeader
          avatar={<DevicesOtherIcon color="primary" />}
          title="Ekipman Yönetimi"
          subheader="Bağlı servisinize ait donanım envanteri (ekipman tanımları Ekipman Admin Yönetimi'nden gelir)"
          action={
            <Box sx={{ display: 'flex', gap: 1, mt: 1, mr: 1, flexWrap: 'wrap' }}>
              <Tooltip title="Görünen (filtrelenmiş) listeyi Excel olarak indir">
                <span>
                  <Button
                    variant="outlined"
                    startIcon={<GetAppIcon />}
                    disabled={filtered.length === 0}
                    onClick={exportExcel}
                  >
                    Excel'e Aktar
                  </Button>
                </span>
              </Tooltip>
              <Tooltip title={!canAdd ? (isAdmin ? 'Yönetici görünümünde ekipman eklenemez' : 'Ekipman ekleme yetkiniz yok') : ''}>
                <span>
                  <Button
                    variant="outlined"
                    color="secondary"
                    startIcon={<PlaylistAddIcon />}
                    disabled={!canAdd || !serviceShopId}
                    onClick={openBulk}
                  >
                    Toplu Ekle
                  </Button>
                </span>
              </Tooltip>
            </Box>
          }
        />
        <Divider />
        <CardContent>
          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center', mt: 0.5 }}>
            {isAdmin ? (
              <>
                <Autocomplete
                  size="small"
                  options={[{ BirimKodu: 0, Dsc: 'Tümü (tüm bölgeler)' },
                    ...bolgeList.filter(b => Number(b.BirimKodu) !== 0
                      && String(b.Dsc || '').trim().toLocaleLowerCase('tr-TR') !== 'tümü')]}
                  getOptionLabel={o => o.Dsc || ''}
                  isOptionEqualToValue={(o, v) => o.BirimKodu === v.BirimKodu}
                  value={selTop
                    ? (bolgeList.find(x => Number(x.BirimKodu) === Number(selTop)) || null)
                    : { BirimKodu: 0, Dsc: 'Tümü (tüm bölgeler)' }}
                  onChange={(e, val) => bolgeSec(val)}
                  sx={{ minWidth: 220 }}
                  renderInput={params => <TextField {...params} variant="outlined" label="Bölge" />}
                />
                <Autocomplete
                  size="small"
                  options={[{ BirimKodu: 0, Dsc: 'Tümü (bölgedeki servisler)' }, ...servisList.filter(s => s.BirimKodu)]}
                  getOptionLabel={o => o.Dsc || ''}
                  isOptionEqualToValue={(o, v) => o.BirimKodu === v.BirimKodu}
                  value={selService
                    ? (servisList.find(x => x.BirimKodu === selService) || null)
                    : { BirimKodu: 0, Dsc: 'Tümü (bölgedeki servisler)' }}
                  onChange={(e, val) => servisSec(val)}
                  disabled={servisList.length === 0}
                  sx={{ minWidth: 240 }}
                  renderInput={params => <TextField {...params} variant="outlined" label="Servis" />}
                />
              </>
            ) : (
              <>
                <Chip color="secondary" label={`Bölge: ${bolgeAdi}`} />
                <Chip color="secondary" label={`Servis: ${servisEtiket || servisAdi}`} />
              </>
            )}
            <Autocomplete
              size="small"
              options={teknisyenOptions}
              getOptionLabel={o => o.Ad || ''}
              isOptionEqualToValue={(o, v) => o.Kod === v.Kod}
              value={filterTeknisyen ? (teknisyenOptions.find(t => String(t.Kod) === String(filterTeknisyen)) || null) : null}
              onChange={(e, val) => { setFilterTeknisyen(val ? val.Kod : ''); setPage(0); }}
              sx={{ minWidth: 200 }}
              renderInput={params => <TextField {...params} variant="outlined" label="Teknisyen" />}
            />
            <Autocomplete
              size="small"
              options={uzmanlikOptions}
              getOptionLabel={o => o || ''}
              value={filterUzmanlik || null}
              onChange={(e, val) => { setFilterUzmanlik(val || ''); setPage(0); }}
              sx={{ minWidth: 200 }}
              renderInput={params => <TextField {...params} variant="outlined" label="Uzmanlık" />}
            />
          </Box>

          <Box sx={styles.toolbar} style={{ marginTop: 16 }}>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
              {isAdmin && (
                <Chip color="secondary" size="small" label={isByYonetici ? 'Bölge Yöneticisi görünümü' : 'Yönetici görünümü'} />
              )}
              {unmatchedCount > 0 && (
                <Chip
                  size="small"
                  clickable
                  sx={styles.warnChip}
                  label={`⚠ Eşleşme bekleyen ${unmatchedCount}`}
                  color={onlyUnmatched ? 'secondary' : 'default'}
                  onClick={() => { setOnlyUnmatched(v => !v); setPage(0); }}
                />
              )}
            </div>
            <Box component="span" sx={styles.spacer} />
            <Tooltip title={!canAdd ? (isAdmin ? 'Yönetici görünümünde ekipman eklenemez' : 'Ekipman ekleme yetkiniz yok') : (!serviceShopId ? 'Servise bağlı değilsiniz' : '')}>
              <span>
                <Button
                  variant="contained"
                  color="secondary"
                  startIcon={<AddIcon />}
                  disabled={!canAdd}
                  onClick={() => {
                    if (!serviceShopId) {
                      notify('warning', 'Kullanıcınız bir servise bağlı olmadığı için ekipman girişi yapamazsınız.');
                      return;
                    }
                    openNew();
                  }}
                >
                  Yeni Ekipman
                </Button>
              </span>
            </Tooltip>
          </Box>

          {/* filtreler */}
          <Box sx={styles.filterBar}>
            <TextField
              sx={styles.searchField}
              size="small"
              variant="outlined"
              placeholder="Ara: ekipman, seri no, donanım no, marka, stok..."
              value={search}
              onChange={e => { setSearch(e.target.value); setPage(0); }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>
                )
              }}
            />
            <FormControl size="small" variant="outlined" style={{ minWidth: 150 }}>
              <InputLabel>Üst Grup</InputLabel>
              <Select label="Üst Grup" value={filterUst}
                onChange={e => { setFilterUst(e.target.value); setFilterGrup(''); setPage(0); }}>
                <MenuItem value="">Tümü</MenuItem>
                {ustGruplarActive.map(u => <MenuItem key={u.Id} value={u.Id}>{u.Aciklama}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControl size="small" variant="outlined" style={{ minWidth: 150 }} disabled={!filterUst}>
              <InputLabel>Grup</InputLabel>
              <Select label="Grup" value={filterGrup}
                onChange={e => { setFilterGrup(e.target.value); setPage(0); }}>
                <MenuItem value="">Tümü</MenuItem>
                {gruplarForFilter.map(g => <MenuItem key={g.Id} value={g.Id}>{g.Aciklama}</MenuItem>)}
              </Select>
            </FormControl>
            <Box sx={styles.statusToggle}>
              <FilterListIcon fontSize="small" sx={{ color: 'text.secondary' }} />
              <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700, mr: 0.5 }}>
                Durum:
              </Typography>
              <Chip label={`Tümü ${list.length}`} clickable size="small"
                variant={filterStatu === 'all' && !onlyExpired ? 'filled' : 'outlined'}
                color={filterStatu === 'all' && !onlyExpired ? 'secondary' : 'default'}
                onClick={() => {
                  setFilterStatu('all');
                  setFilterUst('');
                  setFilterGrup('');
                  setSearch('');
                  setFilterTeknisyen('');
                  setFilterUzmanlik('');
                  setOnlyUnmatched(false);
                  setOnlyExpired(false);
                  setPage(0);
                }} />
              <Chip label={`Alındı ${alindiCount}`} clickable size="small"
                variant={filterStatu === 1 ? 'filled' : 'outlined'}
                color={filterStatu === 1 ? 'secondary' : 'default'}
                onClick={() => { setFilterStatu(s => s === 1 ? 'all' : 1); setPage(0); }} />
              <Chip label={`Alınmadı ${alinmadiCount}`} clickable size="small"
                variant={filterStatu === 0 ? 'filled' : 'outlined'}
                color={filterStatu === 0 ? 'secondary' : 'default'}
                onClick={() => { setFilterStatu(s => s === 0 ? 'all' : 0); setPage(0); }} />
              <Tooltip title="Kalibrasyon süresi (son kalibrasyon + periyot) bugünü geçen ekipmanlar">
                <Chip label={`⏰ Süresi Dolan${expiredCount ? ' ' + expiredCount : ''}`} clickable size="small"
                  variant={onlyExpired ? 'filled' : 'outlined'}
                  color={onlyExpired ? 'error' : 'default'}
                  sx={{ borderColor: '#d32f2f', color: onlyExpired ? undefined : '#d32f2f', fontWeight: 600 }}
                  onClick={() => { setOnlyExpired(v => !v); setPage(0); }} />
              </Tooltip>
            </Box>
          </Box>

          <TableContainer sx={styles.tableWrap}>
            <Table size="small" sx={styles.zebra}>
              <TableHead sx={styles.headRow}>
                <TableRow>
                  <TableCell>Ekipman</TableCell>
                  <TableCell>Grup</TableCell>
                  <TableCell>Stok No</TableCell>
                  <TableCell>Açıklama</TableCell>
                  <TableCell>Seri No</TableCell>
                  <TableCell>Marka / Model</TableCell>
                  <TableCell>Uzmanlık</TableCell>
                  <TableCell align="right">Adet</TableCell>
                  <TableCell>Teknisyen</TableCell>
                  <TableCell>Son Kalib.</TableCell>
                  <TableCell>Durum</TableCell>
                  <TableCell align="center">İşlemler</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filtered.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={12}>
                      <Box sx={styles.emptyState}>
                        <DevicesOtherIcon style={{ fontSize: 48, opacity: 0.4 }} />
                        <Typography variant="body2">
                          {list.length === 0 ? 'Henüz ekipman kaydı yok.' : 'Filtreye uyan kayıt bulunamadı.'}
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                )}
                {paged.map(row => {
                  const c = cinsById.get(row.HardwareType);
                  const cA = c && c.Active ? c : null; // katalog bilgisi sadece aktiften
                  const isSvc = !row.HardwareType; // serviste tanımlı (katalog dışı)
                  const issue = matchIssue(row); // eşleşme sorunu (varsa)
                  const expired = kalibExpired(row); // kalibrasyon süresi dolmuş
                  const uzmList = rowUzmanliklar(row);
                  return (
                    <TableRow key={row.HardwareID}
                      sx={expired
                        ? { backgroundColor: 'rgba(211,47,47,0.12)', boxShadow: 'inset 4px 0 0 0 #d32f2f' }
                        : undefined}>
                      <TableCell>
                        <div style={{ fontWeight: 600 }}>
                          {(c && c.Description) || row.HardwareTypeDescription}
                        </div>
                        {issue && (
                          <Tooltip title="Bu cihazın ekipman eşleşmesi eksik — tıklayıp eşleyin/bağlayın">
                            <Chip
                              size="small"
                              clickable
                              sx={styles.warnChip}
                              style={{ marginTop: 4 }}
                              label={`⚠ ${issue} — Eşleştir`}
                              onClick={() => openEdit(row)}
                            />
                          </Tooltip>
                        )}
                      </TableCell>
                      <TableCell>{isSvc ? grupPathByIds(row.UstGrupId, row.GrupId) : grupPath(row.HardwareType)}</TableCell>
                      <TableCell>{cA ? cA.StokNo : ''}</TableCell>
                      <TableCell>{row.Description}</TableCell>
                      <TableCell>{row.SerialNo}</TableCell>
                      <TableCell>
                        {(isSvc ? [row.Marka, row.Model] : cA ? [cA.Marka, cA.Model] : []).filter(Boolean).join(' / ')}
                      </TableCell>
                      <TableCell>
                        {uzmList.length === 0
                          ? <span style={{ color: '#bbb' }}>—</span>
                          : (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                              {uzmList.map((ad, i) => (
                                <Chip key={i} size="small" sx={styles.uzmChipColored} label={ad} />
                              ))}
                            </Box>
                          )}
                      </TableCell>
                      <TableCell align="right">{row.GivenCount}</TableCell>
                      <TableCell style={{ whiteSpace: 'nowrap' }}>{row.TeknisyenAdi}</TableCell>
                      <TableCell style={{ whiteSpace: 'nowrap' }}>
                        {rowKalibGerekli(row) ? (
                          <>
                            {row.CalibrationDate || '-'}
                            {expired && (
                              <Tooltip title="Kalibrasyon süresi doldu">
                                <span style={{ color: '#d32f2f', fontWeight: 700, marginLeft: 6 }}>⏰</span>
                              </Tooltip>
                            )}
                          </>
                        ) : '-'}
                      </TableCell>
                      <TableCell>
                        {rowKalibGerekli(row) ? (
                          <Chip
                            size="small"
                            sx={row.CalibrationStatu === 1 ? styles.statusAlindi : styles.statusAlinmadi}
                            label={row.CalibrationStatuDescription}
                          />
                        ) : (
                          <Chip size="small" variant="outlined" label="Gerekli değil"
                            sx={{ color: 'text.secondary', borderColor: 'divider' }} />
                        )}
                      </TableCell>
                      <TableCell align="center" style={{ whiteSpace: 'nowrap' }}>
                        <Tooltip title={row.BelgeSayisi > 0 ? `Belgeleri gör (${row.BelgeSayisi})` : 'Belge yok — görüntüle/ekle'}>
                          <IconButton size="small" onClick={() => openGaleri(row)}>
                            <VisibilityIcon fontSize="small" color={row.BelgeSayisi > 0 ? 'primary' : 'disabled'} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Düzenle">
                          <IconButton size="small" onClick={() => openEdit(row)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Log">
                          <IconButton size="small" onClick={() => openLogs(row)}>
                            <HistoryIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Sil">
                          <IconButton
                            size="small"
                            onClick={() =>
                              setConfirmState({
                                open: true,
                                id: row.HardwareID,
                                name: (c && c.Description) || row.HardwareTypeDescription
                              })
                            }
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={filtered.length}
            page={page}
            onPageChange={(e, p) => setPage(p)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={e => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
            rowsPerPageOptions={[10, 25, 50]}
            labelRowsPerPage="Sayfa başına"
          />
        </CardContent>
      </Card>

      {/* Ekle / Düzenle */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth={form.HardwareID === 0 ? 'xl' : 'md'} fullWidth fullScreen={form.HardwareID === 0}>
        <DialogTitle component="div">
          <Box sx={styles.dlgTitle}>
            <DevicesOtherIcon color="secondary" />
            <Typography variant="h5" style={{ fontWeight: 600 }}>
              {form.HardwareID > 0 ? 'Ekipman Düzenle' : 'Yeni Ekipman'}
            </Typography>
            <Tooltip title="Kapat">
              <IconButton onClick={() => setDialogOpen(false)} sx={{ ml: 'auto' }} aria-label="Kapat">
                <CloseIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={3}>
            {form.HardwareID === 0 && (
              <Grid size={{ xs: 12, md: 6 }}>
                {renderGuide()}
              </Grid>
            )}
            <Grid size={{ xs: 12, md: form.HardwareID === 0 ? 6 : 12 }}>
          <Typography sx={styles.sectionTitle} style={{ marginBottom: 8 }}>Ekipman</Typography>

          {/* Seçili ekipman kartı (renkli) — katalog eşleşmesi veya serviste tanımlı */}
          {(matchState === 'matched' || serviceNew) && selectedCins && selectedCins.Active && selectedCins.GrupId && !picking && (
            <Box sx={styles.eqCard}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Avatar sx={styles.eqAvatar}><DevicesOtherIcon fontSize="small" /></Avatar>
                <div style={{ flex: 1 }}>
                  <Box sx={styles.eqName}>
                    {selectedCins.Description}
                    {serviceNew && (
                      <Chip size="small" label="Serviste tanımlı" sx={styles.warnChip}
                        style={{ marginLeft: 8, height: 20 }} />
                    )}
                  </Box>
                  <Box sx={styles.eqGroup}>{grupPathByIds(selectedCins.UstGrupId, selectedCins.GrupId)}</Box>
                </div>
                {serviceNew ? (
                  <div style={{ display: 'flex', gap: 4 }}>
                    <Button size="small" onClick={() => setMatchState('new')}
                      style={{ fontWeight: 700, whiteSpace: 'nowrap' }}>
                      Bilgileri düzenle
                    </Button>
                    {form.HardwareID === 0 && (
                      <Button size="small" onClick={resetMatch}
                        style={{ color: '#e65100', fontWeight: 700, whiteSpace: 'nowrap' }}>
                        ‹ Aramaya dön
                      </Button>
                    )}
                  </div>
                ) : form.HardwareID === 0 ? (
                  <Button size="small" onClick={resetMatch}
                    style={{ color: '#e65100', fontWeight: 700, whiteSpace: 'nowrap' }}>
                    ‹ Aramaya dön
                  </Button>
                ) : (
                  <Button size="small" variant="outlined" color="secondary" startIcon={<SearchIcon />} onClick={startPicking}>
                    Değiştir
                  </Button>
                )}
              </div>

              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 8 }}>
                {pill('Marka', selectedCins.Marka, '#e3f2fd', '#1565c0')}
                {pill('Model', selectedCins.Model, '#f3e5f5', '#6a1b9a')}
                {!serviceNew && pill('Stok No', selectedCins.StokNo, '#e0f2f1', '#00695c')}
                {pill(
                  'Kalibrasyon',
                  selectedCins.KalibrasyonIhtiyaci
                    ? `Gerekli${selectedCins.KalibrasyonGun ? ` · ${selectedCins.KalibrasyonGun} gün` : ''}`
                    : 'Gerekli değil',
                  selectedCins.KalibrasyonIhtiyaci ? '#e8f5e9' : '#eceff1',
                  selectedCins.KalibrasyonIhtiyaci ? '#2e7d32' : '#607d8b'
                )}
              </div>

              {selectedCins.Aciklama && String(selectedCins.Aciklama).trim() && (
                <div style={{ marginTop: 8 }}>
                  <Box component="span" sx={styles.catLabel} style={{ marginRight: 6 }}>Açıklama:</Box>
                  <Box component="span" sx={styles.catValue}>{selectedCins.Aciklama}</Box>
                </div>
              )}

              <div style={{ marginTop: 8 }}>
                <Box component="span" sx={styles.catLabel} style={{ marginRight: 6 }}>Gerekli Uzmanlıklar:</Box>
                {serviceNew ? (
                  (tanim.UzmanlikIds || []).length === 0 ? (
                    <Box component="span" sx={styles.catValue}>-</Box>
                  ) : (
                    uzmanliklar.filter(u => (tanim.UzmanlikIds || []).includes(u.Kod)).map(u => (
                      <Chip key={u.Kod} size="small" sx={styles.uzmChipColored} label={u.Aciklama} />
                    ))
                  )
                ) : cinsUzmanliklari(selectedCins.Id).length === 0 ? (
                  <Box component="span" sx={styles.catValue}>-</Box>
                ) : (
                  cinsUzmanliklari(selectedCins.Id).map(u => (
                    <Chip key={u.UzmanlikId} size="small" sx={styles.uzmChipColored} label={u.Aciklama} />
                  ))
                )}
              </div>
            </Box>
          )}

          {/* Pasif uyarısı */}
          {matchState === 'matched' && selectedCins && !selectedCins.Active && (
            <Box sx={styles.newCard} style={{ borderLeftColor: '#c62828', marginBottom: 12 }}>
              <Box sx={styles.cardHead} style={{ color: '#c62828' }}>
                <InfoOutlinedIcon style={{ fontSize: 16 }} /> Bu ekipman tanımı pasif
              </Box>
              <Typography variant="body2">
                Bağlı ekipman (<b>{selectedCins.Description}</b>) pasif. Lütfen aşağıdan geçerli (aktif) bir ekipman seçin.
              </Typography>
            </Box>
          )}

          {/* Arama kutusu — yeni / değiştir / pasif */}
          {(matchState === 'idle' || picking) && (
          <>
          <Autocomplete
            options={cinsler.filter(c => c.Active)}
            value={selectedCins && selectedCins.Active ? selectedCins : null}
            inputValue={stokInput}
            onInputChange={(e, val, reason) => { if (reason !== 'reset') setStokInput(val); }}
            getOptionLabel={o => (o && o.__new ? o.input : (o && o.Description) || '')}
            isOptionEqualToValue={(o, v) => v && o.Id === v.Id}
            filterOptions={filterEkipmanOptions}
            onChange={(e, val) => handlePick(val)}
            noOptionsText="Stok no veya ekipman adı yazın…"
            renderOption={(props, o) =>
              o.__new ? (
                <li {...props} key="__new">
                  <div style={{ color: '#e65100', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}>
                    <AddIcon fontSize="small" /> Arçelik sisteminde tanımlı olmayan yeni ekipman girişi
                  </div>
                </li>
              ) : (
                <li {...props} key={o.Id}>
                  <div>
                    <div style={{ fontWeight: 600 }}>{o.Description}</div>
                    <div style={{ fontSize: 12, color: '#777' }}>
                      {[o.StokNo && 'Stok: ' + o.StokNo, grupPath(o.Id), o.Marka].filter(Boolean).join(' · ')}
                    </div>
                  </div>
                </li>
              )
            }
            renderInput={params => (
              <TextField
                {...params}
                variant="outlined"
                size="small"
                label="Stok No yazın veya ekipman arayın"
                placeholder="örn. 9197066090 ya da Multimetre"
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <>
                      <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>
                      {params.InputProps.startAdornment}
                    </>
                  )
                }}
              />
            )}
          />
          <div style={{ display: 'flex', alignItems: 'center', marginTop: 4 }}>
            <Typography variant="caption" color="textSecondary" style={{ flex: 1 }}>
              Stok no ya da ekipman adı yazın; yoksa listenin en üstündeki <b>“Arçelik sisteminde tanımlı olmayan yeni ekipman girişi”</b>ni seçin.
            </Typography>
            {picking && selectedCins && selectedCins.Active && selectedCins.GrupId && (
              <Button size="small" onClick={() => setPicking(false)}>Vazgeç</Button>
            )}
          </div>
          </>
          )}

          {/* AKTİF ama GRUPSUZ -> gruba bağla */}
          {matchState === 'matched' && selectedCins && selectedCins.Active && !selectedCins.GrupId && (
            <Box sx={styles.newCard} style={{ borderLeftColor: '#f9a825' }}>
              <Box sx={styles.cardHead} style={{ color: '#f57f17' }}>
                <InfoOutlinedIcon style={{ fontSize: 16 }} /> Ekipman gruba bağlı değil
              </Box>
              <Typography variant="body2" style={{ marginBottom: 8 }}>
                <b>{selectedCins.Description}</b>
                {selectedCins.StokNo ? ` (Stok: ${selectedCins.StokNo})` : ''} aktif ama bir gruba bağlı değil.
                Devam etmek için üst grup ve grup seçip bağlayın.
              </Typography>
              <Grid container spacing={2} alignItems="center">
                <Grid size={{ xs: 12, sm: 5 }}>
                  <FormControl fullWidth variant="outlined" size="small">
                    <InputLabel>Üst Grup</InputLabel>
                    <Select label="Üst Grup" value={tanim.UstGrupId}
                      onChange={e => setTanim(t => ({ ...t, UstGrupId: e.target.value, GrupId: '' }))}>
                      {ustGruplarActive.map(u => <MenuItem key={u.Id} value={u.Id}>{u.Aciklama}</MenuItem>)}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid size={{ xs: 12, sm: 5 }}>
                  <FormControl fullWidth variant="outlined" size="small" disabled={!tanim.UstGrupId}>
                    <InputLabel>Grup</InputLabel>
                    <Select label="Grup" value={tanim.GrupId}
                      onChange={e => setTanimField('GrupId', e.target.value)}>
                      {tanimGruplarForUst.map(g => <MenuItem key={g.Id} value={g.Id}>{g.Aciklama}</MenuItem>)}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid size={{ xs: 12, sm: 2 }}>
                  <Button variant="contained" color="secondary" fullWidth onClick={bindGroup}>
                    Gruba Bağla
                  </Button>
                </Grid>
              </Grid>
            </Box>
          )}

          {/* Eşleşme yok -> yeni ekipman tanımı (adım adım) */}
          {matchState === 'new' && (
            <Box sx={styles.wizardBox}>
              <Box sx={styles.wizardHead}>
                <AddIcon fontSize="small" />
                <span style={{ flex: 1 }}>
                  Arçelik sisteminde tanımlı olmayan yeni ekipman girişi
                </span>
                <Button size="small" onClick={resetMatch}
                  style={{ color: '#e65100', fontWeight: 700, whiteSpace: 'nowrap' }}>
                  ‹ Aramaya dön
                </Button>
              </Box>

              {/* Tek pencere düz form — Ekipman Tanımı */}
              <Typography sx={styles.sectionTitle} style={{ margin: '2px 0 8px' }}>Ekipman Tanımı</Typography>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField label="Ekipman Adı" required variant="outlined" size="small" fullWidth
                    value={tanim.Description}
                    onChange={e => setTanimField('Description', e.target.value)} />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField label="Stok No" variant="outlined" size="small" fullWidth
                    value={form.StokNo}
                    onChange={e => setField('StokNo', e.target.value)}
                    helperText="Arçelik stok kodu yok ise boş bırakınız" />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <FormControl fullWidth variant="outlined" size="small">
                    <InputLabel>Üst Grup *</InputLabel>
                    <Select label="Üst Grup *" value={tanim.UstGrupId}
                      onChange={e => setTanim(t => ({ ...t, UstGrupId: e.target.value, GrupId: '' }))}>
                      {ustGruplarActive.map(u => <MenuItem key={u.Id} value={u.Id}>{u.Aciklama}</MenuItem>)}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <FormControl fullWidth variant="outlined" size="small" disabled={!tanim.UstGrupId}>
                    <InputLabel>Grup *</InputLabel>
                    <Select label="Grup *" value={tanim.GrupId}
                      onChange={e => setTanimField('GrupId', e.target.value)}>
                      {tanimGruplarForUst.map(g => <MenuItem key={g.Id} value={g.Id}>{g.Aciklama}</MenuItem>)}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid size={{ xs: 6, sm: 4 }}>
                  <TextField label="Marka" variant="outlined" size="small" fullWidth
                    value={tanim.Marka} onChange={e => setTanimField('Marka', e.target.value)} />
                </Grid>
                <Grid size={{ xs: 6, sm: 4 }}>
                  <TextField label="Model" variant="outlined" size="small" fullWidth
                    value={tanim.Model} onChange={e => setTanimField('Model', e.target.value)} />
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <FormControlLabel
                    control={<Switch color="primary" checked={tanim.KalibrasyonIhtiyaci}
                      onChange={e => {
                        const on = e.target.checked;
                        setTanimField('KalibrasyonIhtiyaci', on);
                        if (on) setField('CalibrationStatu', 1); // ihtiyaç işaretlenince otomatik "Var"
                      }} />}
                    label="Kalibrasyon İhtiyacı" />
                </Grid>
              </Grid>
              {benzerEkipmanlar.length > 0 && (
                <Box style={{ marginTop: 12, padding: 12, borderRadius: 10, border: '1px solid #ffcc80', backgroundColor: '#fff8e1' }}>
                  <Box style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#e65100', fontWeight: 700, fontSize: 13 }}>
                    <InfoOutlinedIcon style={{ fontSize: 16 }} /> Benzer tanımlı ekipmanlar — bunlardan birini mi kastettiniz?
                  </Box>
                  <Typography variant="body2" color="textSecondary" style={{ margin: '4px 0 6px' }}>
                    Aynı ekipmanı tekrar oluşturmayın; varsa mevcut olanı seçin.
                  </Typography>
                  {benzerEkipmanlar.map(c => {
                    const tam = (qAdNorm && normalizeText(c.Description) === qAdNorm)
                      || (qStokNorm && normalizeText(c.StokNo) === qStokNorm);
                    return (
                      <Box key={c.Id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0', borderTop: '1px dashed #ffe0b2' }}>
                        <Box style={{ flex: 1, minWidth: 0 }}>
                          <Box style={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}>
                            {c.Description}
                            {tam && <Chip size="small" label="zaten tanımlı" sx={styles.warnChip} style={{ height: 18 }} />}
                          </Box>
                          <Box style={{ fontSize: 12, color: '#777' }}>
                            {[c.StokNo && ('Stok: ' + c.StokNo), grupPath(c.Id), c.Marka].filter(Boolean).join(' · ')}
                          </Box>
                        </Box>
                        <Button size="small" variant="outlined" color="secondary" onClick={() => handlePick(c)}
                          style={{ whiteSpace: 'nowrap' }}>
                          Bunu seç
                        </Button>
                      </Box>
                    );
                  })}
                </Box>
              )}
            </Box>
          )}

          {/* CİHAZ BİLGİLERİ — ekipman seçili ve bağlıyken */}
          {(deviceReady || matchState === 'new') && !picking && (
            <>
              <Divider style={{ margin: '16px 0 10px' }} />
              {justDefined && (
                <Box sx={styles.greenBanner}>
                  <CheckCircleIcon fontSize="small" /> Ekipman tanımlandı ve seçildi — şimdi bu cihaz için servis kaydını girip <b>Kaydet</b>'e basın.
                </Box>
              )}
              <Typography sx={styles.sectionTitle} style={{ marginBottom: 8 }}>Cihaz Bilgileri</Typography>
              <Grid container spacing={2}>
                {/* Açıklama · Seri No · Adet — yan yana */}
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField label="Açıklama" variant="outlined" size="small" fullWidth
                    value={form.Description} onChange={e => setField('Description', e.target.value)} />
                </Grid>
                <Grid size={{ xs: 8, sm: 4 }}>
                  <TextField label="Seri No" variant="outlined" size="small" fullWidth
                    value={form.SerialNo} onChange={e => setField('SerialNo', e.target.value)} />
                </Grid>
                <Grid size={{ xs: 4, sm: 2 }}>
                  <TextField label="Adet" type="number" variant="outlined" size="small" fullWidth
                    inputProps={{ min: 0 }} value={form.GivenCount}
                    onChange={e => setField('GivenCount', e.target.value)} />
                </Grid>

                {/* Kalibrasyon — tanıma göre: gerekli değilse pasif, gerekliyse zorunlu.
                    Yeni ekipman (düz form) modunda "gerektirmiyor" bandı gösterilmez;
                    anahtar zaten üstte olduğu için gereksiz/kafa karıştırıcı. */}
                {!kalibGerekliEff && matchState !== 'new' && (
                  <Grid size={12}>
                    <Box sx={styles.infoBanner}>
                      <InfoOutlinedIcon fontSize="small" />
                      Bu ekipman kalibrasyon gerektirmiyor — kalibrasyon bilgisi girilmesine gerek yok.
                    </Box>
                  </Grid>
                )}
                {kalibGerekliEff && (
                  <Grid size={12}>
                    <Box sx={styles.warnBanner}>
                      <InfoOutlinedIcon fontSize="small" />
                      Bu ekipman için <b>kalibrasyon zorunludur</b> — kalibrasyon durumu, süresi ve son kalibrasyon tarihini girin.
                    </Box>
                  </Grid>
                )}

                {/* Kalibrasyon: Durum (Var/Yok) · Süre (gün) · Son Kalibrasyon Tarihi */}
                <Grid size={{ xs: 12, sm: 4 }}>
                  <Typography variant="caption" color="textSecondary">
                    Kalibrasyon Durumu{kalibGerekliEff ? ' *' : ''}
                  </Typography>
                  <Box sx={styles.statusToggle}>
                    <Chip label="Var" clickable={kalibGerekliEff} disabled={!kalibGerekliEff}
                      color={form.CalibrationStatu === 1 ? 'secondary' : 'default'}
                      icon={form.CalibrationStatu === 1 ? <CheckCircleIcon /> : undefined}
                      onClick={() => kalibGerekliEff && setField('CalibrationStatu', 1)} />
                    <Chip label="Yok" clickable={kalibGerekliEff} disabled={!kalibGerekliEff}
                      color={form.CalibrationStatu === 0 ? 'secondary' : 'default'}
                      icon={form.CalibrationStatu === 0 ? <CheckCircleIcon /> : undefined}
                      onClick={() => {
                        if (!kalibGerekliEff) return;
                        setField('CalibrationStatu', 0);
                        notify('warning', "Bu ekipmanda 'Kalibrasyon ihtiyacı' işaretli — durumu 'Yok' seçtiniz.");
                      }} />
                  </Box>
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <TextField label={`Kalibrasyon Süresi (gün)${kalibGerekliEff ? ' *' : ''}`}
                    type="number" variant="outlined" size="small" fullWidth disabled={!kalibGerekliEff}
                    inputProps={{ min: 0 }} value={form.CalibrationDuration}
                    onChange={e => setField('CalibrationDuration', e.target.value.replace(/[^0-9]/g, ''))} />
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <TextField label={`Son Kalibrasyon Tarihi${kalibGerekliEff && Number(form.CalibrationStatu) === 1 ? ' *' : ''}`}
                    type="date" variant="outlined" size="small" fullWidth
                    disabled={!kalibGerekliEff || Number(form.CalibrationStatu) !== 1}
                    helperText={kalibGerekliEff && Number(form.CalibrationStatu) !== 1 ? 'Kalibrasyon alınmadıysa tarih gerekmez' : undefined}
                    InputLabelProps={{ shrink: true }} value={form.CalibrationDate}
                    onChange={e => setField('CalibrationDate', e.target.value)} />
                </Grid>

                {/* Teknisyen — servise bağlı teknisyenler (arayıp aşağı açılır) */}
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Autocomplete
                    size="small"
                    options={teknisyenler}
                    value={teknisyenler.find(t => t.Id === form.TeknisyenKodu) || null}
                    getOptionLabel={o => o.AdSoyad || ''}
                    isOptionEqualToValue={(o, v) => o.Id === v.Id}
                    onChange={(e, val) => setField('TeknisyenKodu', val ? val.Id : '')}
                    renderOption={(props, o) => <li {...props} key={o.Id}>{o.AdSoyad}</li>}
                    componentsProps={{ popper: { placement: 'bottom-start', modifiers: [{ name: 'flip', enabled: false }, { name: 'preventOverflow', enabled: false }] } }}
                    renderInput={params => (
                      <TextField {...params} variant="outlined" label="Teknisyen"
                        placeholder="Teknisyen seçin…" />
                    )}
                  />
                </Grid>

                {/* Uzmanlık — teknisyenin yanında (serviste tanımlı ekipmanda) */}
                {(matchState === 'new' || serviceNew) && (
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Autocomplete
                      multiple
                      size="small"
                      options={uzmanliklar}
                      value={uzmanliklar.filter(u => (tanim.UzmanlikIds || []).includes(u.Kod))}
                      getOptionLabel={o => o.Aciklama || ''}
                      isOptionEqualToValue={(o, v) => o.Kod === v.Kod}
                      onChange={(e, vals) => setTanimField('UzmanlikIds', vals.map(v => v.Kod))}
                      renderOption={(props, o) => <li {...props} key={o.Kod}>{o.Aciklama}</li>}
                      renderInput={params => (
                        <TextField {...params} variant="outlined" label="Uzmanlık"
                          placeholder="Uzmanlık ekleyin…" />
                      )}
                    />
                  </Grid>
                )}
              </Grid>

              {/* Belgeler — çoklu (foto, kalibrasyon belgesi, etiket, fatura, diğer) */}
              <Divider style={{ margin: '16px 0 10px' }} />
              <Box sx={styles.sectionHead}>
                <PhotoCameraIcon fontSize="small" color="action" />
                <Typography variant="body2" color="textSecondary">Belgeler</Typography>
              </Box>
              <DosyaYukleyici
                hint={DOSYA_HINT}
                yeni={yeniDosyalar}
                setYeni={setYeniDosyalar}
                mevcut={mevcutDosyalar}
                onMevcutSil={silMevcutDosya}
                onGoster={gosterDosya}
                kapakAktif={false}
                tipZorunlu
              />
            </>
          )}
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions
          sx={{
            position: 'sticky',
            bottom: 0,
            zIndex: 2,
            px: 2.5,
            py: 1.25,
            gap: 1,
            borderTop: '1px solid',
            borderColor: 'divider',
            backgroundColor: '#eef2f6',
            boxShadow: '0 -2px 8px rgba(0,0,0,0.06)'
          }}
        >
          <Button
            variant="contained"
            color="secondary"
            onClick={() => setDialogOpen(false)}
            startIcon={<CloseIcon />}
            sx={{ fontWeight: 700 }}
          >
            Vazgeç
          </Button>
          <Button
            variant="contained"
            color="secondary"
            onClick={save}
            startIcon={<CheckCircleIcon />}
            sx={{ fontWeight: 700 }}
          >
            Kaydet
          </Button>
        </DialogActions>
      </Dialog>

      {/* Silme onayı */}
      <Dialog open={confirmState.open} onClose={() => setConfirmState({ open: false, id: 0, name: '' })}>
        <DialogTitle>Silme Onayı</DialogTitle>
        <DialogContent>
          <Typography>"{confirmState.name}" kaydını silmek istediğinize emin misiniz?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmState({ open: false, id: 0, name: '' })}>Vazgeç</Button>
          <Button variant="contained" color="secondary" onClick={doDelete}>Sil</Button>
        </DialogActions>
      </Dialog>

      {/* Log */}
      <Dialog open={logOpen} onClose={() => setLogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Donanım Log</DialogTitle>
        <DialogContent dividers>
          <TableContainer sx={styles.tableWrap}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Tarih</TableCell>
                  <TableCell>İşlem</TableCell>
                  <TableCell>Eski Değer</TableCell>
                  <TableCell>Yeni Değer</TableCell>
                  <TableCell>Kullanıcı</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {logRows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      <Typography variant="body2" color="textSecondary">Log kaydı yok.</Typography>
                    </TableCell>
                  </TableRow>
                )}
                {logRows.map(l => (
                  <TableRow key={l.Id}>
                    <TableCell>{l.KayitTarihi}</TableCell>
                    <TableCell>{l.Islem}</TableCell>
                    <TableCell>{l.EskiDeger}</TableCell>
                    <TableCell>{l.YeniDeger}</TableCell>
                    <TableCell>{l.KayitEden}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLogOpen(false)}>Kapat</Button>
        </DialogActions>
      </Dialog>

      {/* Ekipman belge galerisi */}
      <Dialog open={galeri.open} onClose={() => setGaleri(g => ({ ...g, open: false }))} maxWidth="md" fullWidth>
        <DialogTitle disableTypography>
          <div className={undefined} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <ImageIcon color="secondary" />
            <Typography variant="h6" style={{ fontWeight: 600 }}>{galeri.baslik} — Belgeler</Typography>
          </div>
        </DialogTitle>
        <DialogContent dividers>
          {galeri.loading ? (
            <Typography variant="body2" color="textSecondary">Belgeler yükleniyor…</Typography>
          ) : galeri.items.length === 0 ? (
            <Typography variant="body2" color="textSecondary">Bu ekipmana ait belge yok.</Typography>
          ) : (
            <Grid container spacing={2}>
              {galeri.items.map((it, i) => {
                const img = /^image\//i.test(it.ContentType || '');
                const ac = () => {
                  if (img && it.base64) openFoto(it.base64);
                  else if (it.dataUrl) window.open(it.dataUrl, '_blank');
                };
                return (
                  <Grid key={it.Id || i} size={{ xs: 6, sm: 4, md: 3 }}>
                    <Box onClick={ac} style={{ cursor: 'pointer', border: '1px solid #e0e0e0', borderRadius: 8, overflow: 'hidden' }}>
                      <Box style={{ height: 120, background: '#fafafa', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        {img && it.dataUrl ? (
                          <img src={it.dataUrl} alt={it.DosyaAdi}
                            style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                        ) : (
                          <ImageIcon style={{ fontSize: 40, opacity: 0.4 }} />
                        )}
                      </Box>
                      <Box style={{ padding: 6 }}>
                        <Chip size="small" color="secondary" label={dosyaTipiLabel(it.Tip)} style={{ height: 18 }} />
                        <div style={{ fontSize: 12, color: '#555', marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {it.DosyaAdi || 'Belge'}
                        </div>
                      </Box>
                    </Box>
                  </Grid>
                );
              })}
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setGaleri(g => ({ ...g, open: false }))}>Kapat</Button>
        </DialogActions>
      </Dialog>

      {/* Foto büyük */}
      <Dialog open={fotoOpen} onClose={() => setFotoOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Dosya / Foto</DialogTitle>
        <DialogContent dividers>
          <Box component="img" sx={styles.fotoPreview} src={'data:image/*;base64,' + fotoData} alt="donanım"
            onError={e => { e.target.style.display = 'none'; }} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFotoOpen(false)}>Kapat</Button>
        </DialogActions>
      </Dialog>

      {/* Toplu Ekle — satır satır */}
      <Dialog open={bulkOpen} onClose={() => setBulkOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle component="div">
          <Box sx={styles.dlgTitle}>
            <PlaylistAddIcon color="secondary" />
            <Typography variant="h5" style={{ fontWeight: 600 }}>Toplu Ekipman Ekle</Typography>
          </Box>
        </DialogTitle>
        <DialogContent dividers sx={{ backgroundColor: '#f4f6f9' }}>
          <Typography variant="body2" color="textSecondary" style={{ marginBottom: 12 }}>
            Ekipmanı seçip <b>Ekle</b>'ye (ya da Enter) basın; aşağıdaki listede birikir. Hepsi tamamsa
            <b> Kaydet</b>'e basın. Kayıtlar bağlı servisinize (<b>{servisAdi}</b>) eklenir.
          </Typography>

          {/* ÜST: tek giriş (composer) */}
          {bulkDraft && (() => {
            const kalib = bulkRowKalib(bulkDraft);
            const grupOptions = gruplar.filter(g => g.Active && g.UstGrupId === bulkDraft.UstGrupId);
            const onEnter = e => { if (e.key === 'Enter') { e.preventDefault(); bulkAddDraft(); } };
            return (
              <Box sx={{
                borderRadius: 2.5, p: 1.5, mb: 2, backgroundColor: 'background.paper',
                border: '1px solid', borderColor: 'secondary.light',
                borderLeft: '4px solid', borderLeftColor: 'secondary.main',
                boxShadow: '0 1px 4px rgba(16,32,48,.08)'
              }}>
                <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '.04em' }}>
                  Ekipman ekle
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'flex-start', mt: 0.75 }}>
                  <Autocomplete
                    size="small"
                    options={cinsler.filter(c => c.Active)}
                    value={bulkDraft.cins}
                    inputValue={bulkDraft.input}
                    onInputChange={(e, val, reason) => {
                      if (reason === 'reset') return;
                      // yeni ekipman modundaysa yazdıkça adı da güncelle
                      setBulkDraft(d => ({ ...d, input: val, ...(d.isNew ? { Description: val } : {}) }));
                    }}
                    getOptionLabel={o => (o && o.__new ? o.input : (o && o.Description) || '')}
                    isOptionEqualToValue={(o, v) => v && o.Id === v.Id}
                    filterOptions={filterEkipmanOptions}
                    onChange={(e, val) => draftPick(val)}
                    noOptionsText="Kod veya ekipman adı yazın…"
                    clearOnBlur={false}
                    onBlur={() => {
                      // yazıp seçmeden başka alana geçilirse yazılanı yeni ekipman olarak al (silinmesin)
                      if (bulkDraft && !bulkDraft.cins && !bulkDraft.isNew) {
                        const t = String(bulkDraft.input || '').trim();
                        if (t) draftPick({ __new: true, input: t });
                      }
                    }}
                    sx={{ minWidth: 240, flex: 1 }}
                    renderOption={(props, o) =>
                      o.__new ? (
                        <li {...props} key="__new">
                          <div style={{ color: '#e65100', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}>
                            <AddIcon fontSize="small" /> Listede yok — yeni ekipman ekle
                          </div>
                        </li>
                      ) : (
                        <li {...props} key={o.Id}>
                          <div>
                            <div style={{ fontWeight: 600 }}>{o.Description}</div>
                            <div style={{ fontSize: 12, color: '#777' }}>
                              {[o.StokNo && 'Kod: ' + o.StokNo, o.Marka].filter(Boolean).join(' · ')}
                            </div>
                          </div>
                        </li>
                      )
                    }
                    renderInput={params => (
                      <TextField {...params} variant="outlined" size="small" label="Ekipman (kod ya da ad)"
                        placeholder="örn. Multimetre"
                        InputProps={{
                          ...params.InputProps,
                          startAdornment: (
                            <>
                              <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>
                              {params.InputProps.startAdornment}
                            </>
                          )
                        }} />
                    )}
                  />

                  <TextField label="Seri No" size="small" sx={{ width: 120 }}
                    value={bulkDraft.SerialNo} onChange={e => draftPatch({ SerialNo: e.target.value })} onKeyDown={onEnter} />

                  <TextField label="Adet" type="number" size="small" sx={{ width: 80 }}
                    inputProps={{ min: 0 }} value={bulkDraft.GivenCount}
                    onChange={e => draftPatch({ GivenCount: e.target.value })} onKeyDown={onEnter} />

                  <Autocomplete size="small" sx={{ minWidth: 180, flex: 1 }}
                    options={teknisyenOptions}
                    getOptionLabel={o => o.Ad || ''}
                    isOptionEqualToValue={(o, v) => o.Kod === v.Kod}
                    value={bulkDraft.TeknisyenKodu ? (teknisyenOptions.find(t => String(t.Kod) === String(bulkDraft.TeknisyenKodu)) || null) : null}
                    onChange={(e, val) => draftPatch({ TeknisyenKodu: val ? val.Kod : '' })}
                    renderInput={p => <TextField {...p} variant="outlined" size="small" label="Teknisyen" />} />
                </Box>

                {bulkDraft.cins && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.75, ml: 0.5, color: 'success.main' }}>
                    <CheckCircleIcon sx={{ fontSize: 15 }} />
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      katalogtan · {[bulkDraft.cins.StokNo && 'Kod: ' + bulkDraft.cins.StokNo, bulkDraft.cins.Marka,
                        bulkDraft.cins.KalibrasyonIhtiyaci ? `kalibrasyon ${bulkDraft.cins.KalibrasyonGun || 0} gün (otomatik)` : 'kalibrasyon gerekmiyor']
                        .filter(Boolean).join(' · ')}
                    </Typography>
                  </Box>
                )}

                {bulkDraft.isNew && (
                  <Box sx={{ mt: 1, p: 1.25, borderRadius: 1.5, backgroundColor: 'rgba(156,39,176,0.05)' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1 }}>
                      <Chip size="small" color="secondary" label="yeni ekipman" sx={{ height: 20, fontWeight: 600 }} />
                      <Typography variant="caption" color="textSecondary">listede yok — grubunu ve bilgilerini gir</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                      <FormControl size="small" sx={{ minWidth: 150 }}>
                        <InputLabel>Üst Grup *</InputLabel>
                        <Select label="Üst Grup *" value={bulkDraft.UstGrupId}
                          onChange={e => draftPatch({ UstGrupId: e.target.value, GrupId: '' })}>
                          {ustGruplarActive.map(u => <MenuItem key={u.Id} value={u.Id}>{u.Aciklama}</MenuItem>)}
                        </Select>
                      </FormControl>
                      <FormControl size="small" sx={{ minWidth: 150 }} disabled={!bulkDraft.UstGrupId}>
                        <InputLabel>Grup *</InputLabel>
                        <Select label="Grup *" value={bulkDraft.GrupId}
                          onChange={e => draftPatch({ GrupId: e.target.value })}>
                          {grupOptions.map(g => <MenuItem key={g.Id} value={g.Id}>{g.Aciklama}</MenuItem>)}
                        </Select>
                      </FormControl>
                      <TextField label="Marka" size="small" sx={{ width: 120 }}
                        value={bulkDraft.Marka} onChange={e => draftPatch({ Marka: e.target.value })} />
                      <TextField label="Model" size="small" sx={{ width: 120 }}
                        value={bulkDraft.Model} onChange={e => draftPatch({ Model: e.target.value })} />
                      <Box>
                        <Typography variant="caption" color="textSecondary" sx={{ display: 'block' }}>Kalibrasyon ihtiyacı</Typography>
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          <Chip size="small" label="Var" clickable
                            color={bulkDraft.KalibrasyonIhtiyaci ? 'secondary' : 'default'}
                            variant={bulkDraft.KalibrasyonIhtiyaci ? 'filled' : 'outlined'}
                            icon={bulkDraft.KalibrasyonIhtiyaci ? <CheckCircleIcon /> : undefined}
                            onClick={() => draftPatch({ KalibrasyonIhtiyaci: true })} />
                          <Chip size="small" label="Yok" clickable
                            color={!bulkDraft.KalibrasyonIhtiyaci ? 'secondary' : 'default'}
                            variant={!bulkDraft.KalibrasyonIhtiyaci ? 'filled' : 'outlined'}
                            icon={!bulkDraft.KalibrasyonIhtiyaci ? <CheckCircleIcon /> : undefined}
                            onClick={() => draftPatch({ KalibrasyonIhtiyaci: false, KalibrasyonGun: '', CalibrationDate: '' })} />
                        </Box>
                      </Box>
                      {bulkDraft.KalibrasyonIhtiyaci && (
                        <TextField label="Kalib. gün" type="number" size="small" sx={{ width: 100 }}
                          inputProps={{ min: 0 }} value={bulkDraft.KalibrasyonGun}
                          onChange={e => draftPatch({ KalibrasyonGun: e.target.value })} />
                      )}
                      <Autocomplete multiple size="small" sx={{ minWidth: 220, flex: 1 }}
                        options={uzmanliklar}
                        value={uzmanliklar.filter(u => (bulkDraft.UzmanlikIds || []).includes(u.Kod))}
                        getOptionLabel={o => o.Aciklama || ''}
                        isOptionEqualToValue={(o, v) => o.Kod === v.Kod}
                        onChange={(e, vals) => draftPatch({ UzmanlikIds: vals.map(v => v.Kod) })}
                        renderInput={p => <TextField {...p} variant="outlined" size="small" label="Uzmanlık" />} />
                    </Box>

                    {/* katalogda benzer var mı? — bunu mu demek istedin? */}
                    {(() => {
                      const q = normalizeText(bulkDraft.Description);
                      if (q.length < 2) return null;
                      const benzer = cinsler
                        .filter(c => c.Active && c.GrupId && normalizeText(c.Description).includes(q))
                        .slice(0, 5);
                      if (benzer.length === 0) return null;
                      return (
                        <Box sx={{ mt: 1, p: 1, borderRadius: 1, backgroundColor: 'rgba(237,108,2,0.08)', border: '1px solid', borderColor: 'warning.light' }}>
                          <Typography variant="caption" sx={{ color: 'warning.dark', fontWeight: 700, display: 'block', mb: 0.5 }}>
                            ⚠ Katalogda benzer ekipman var — bunlardan biri mi? (tıklayıp seçebilirsin)
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                            {benzer.map(c => (
                              <Chip key={c.Id} size="small" variant="outlined" clickable
                                label={c.Description + (c.StokNo ? ` (${c.StokNo})` : '')}
                                onClick={() => draftPick(c)} />
                            ))}
                          </Box>
                        </Box>
                      );
                    })()}
                  </Box>
                )}

                {kalib && (
                  <Box sx={{ mt: 1, display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                    <Box>
                      <Typography variant="caption" color="textSecondary" sx={{ display: 'block' }}>Kalibrasyon Durumu</Typography>
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <Chip size="small" label="Var" clickable
                          color={Number(bulkDraft.CalibrationStatu) === 1 ? 'secondary' : 'default'}
                          variant={Number(bulkDraft.CalibrationStatu) === 1 ? 'filled' : 'outlined'}
                          icon={Number(bulkDraft.CalibrationStatu) === 1 ? <CheckCircleIcon /> : undefined}
                          onClick={() => draftPatch({ CalibrationStatu: 1 })} />
                        <Chip size="small" label="Yok" clickable
                          color={Number(bulkDraft.CalibrationStatu) !== 1 ? 'secondary' : 'default'}
                          variant={Number(bulkDraft.CalibrationStatu) !== 1 ? 'filled' : 'outlined'}
                          icon={Number(bulkDraft.CalibrationStatu) !== 1 ? <CheckCircleIcon /> : undefined}
                          onClick={() => draftPatch({ CalibrationStatu: 0, CalibrationDate: '' })} />
                      </Box>
                    </Box>
                    <TextField label={`Son Kalibrasyon${Number(bulkDraft.CalibrationStatu) === 1 ? ' *' : ''}`}
                      type="date" size="small" sx={{ width: 175 }}
                      disabled={Number(bulkDraft.CalibrationStatu) !== 1} InputLabelProps={{ shrink: true }}
                      value={bulkDraft.CalibrationDate} onChange={e => draftPatch({ CalibrationDate: e.target.value })} />
                  </Box>
                )}

                {(bulkDraft.cins || bulkDraft.isNew) && (
                  <TextField label="Açıklama" size="small" fullWidth sx={{ mt: 1 }}
                    value={bulkDraft.Aciklama} onChange={e => draftPatch({ Aciklama: e.target.value })} onKeyDown={onEnter} />
                )}

                {(bulkDraft.cins || bulkDraft.isNew) && (
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary' }}>
                      Belgeler — en az 1 fotoğraf zorunlu
                    </Typography>
                    <DosyaYukleyici
                      yeni={bulkDraft.files}
                      setYeni={arr => draftPatch({ files: arr })}
                      mevcut={[]}
                      kapakAktif={false}
                      tipZorunlu
                      onGoster={(k, item) => { if (item && item.base64) openFoto(item.base64); }}
                    />
                  </Box>
                )}

                <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 1.5, mt: 1.25 }}>
                  {!draftReady && bulkRowDolu(bulkDraft) && (
                    <Typography variant="caption" sx={{ color: 'warning.main', fontWeight: 600 }}>
                      ⚠ {bulkRowHata(bulkDraft)}
                    </Typography>
                  )}
                  <Button variant="contained" color="secondary" startIcon={<CheckCircleIcon />}
                    disabled={!draftReady} onClick={bulkAddDraft} sx={{ fontWeight: 700 }}>
                    Ekle
                  </Button>
                </Box>
              </Box>
            );
          })()}

          {/* ALT: eklenenler tablosu */}
          <Typography variant="caption" sx={{ display: 'block', fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '.04em', mb: 0.75 }}>
            Eklenenler ({bulkList.length})
          </Typography>
          {bulkList.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 3, color: 'text.secondary', border: '1px dashed', borderColor: 'divider', borderRadius: 2, backgroundColor: 'background.paper' }}>
              <Typography variant="body2">Henüz ekipman eklenmedi. Yukarıdan seçip <b>Ekle</b>'ye basın.</Typography>
            </Box>
          ) : (
            <TableContainer component={Box} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, backgroundColor: 'background.paper' }}>
              <Table size="small">
                <TableHead sx={styles.headRow}>
                  <TableRow>
                    <TableCell sx={{ width: 40 }}>#</TableCell>
                    <TableCell>Ekipman</TableCell>
                    <TableCell sx={{ width: 110 }}>Seri No</TableCell>
                    <TableCell sx={{ width: 60 }}>Adet</TableCell>
                    <TableCell sx={{ width: 120 }}>Son Kalib.</TableCell>
                    <TableCell>Açıklama</TableCell>
                    <TableCell sx={{ width: 90 }}>Tür</TableCell>
                    <TableCell sx={{ width: 84 }} />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {bulkList.map((it, idx) => {
                    const kalib = bulkRowKalib(it);
                    const ad = it.isNew ? it.Description : (it.cins ? it.cins.Description : '');
                    const marka = it.isNew ? it.Marka : (it.cins ? it.cins.Marka : '');
                    const kod = it.cins ? it.cins.StokNo : '';
                    const fotoN = (it.files || []).length;
                    const alt = [kod && 'Kod: ' + kod, marka, fotoN ? `📷 ${fotoN}` : null].filter(Boolean).join(' · ');
                    return (
                      <TableRow key={it.uid} hover>
                        <TableCell sx={{ color: 'text.disabled', fontVariantNumeric: 'tabular-nums' }}>{idx + 1}</TableCell>
                        <TableCell sx={{ borderLeft: '3px solid', borderLeftColor: it.isNew ? 'secondary.main' : 'success.main' }}>
                          <div style={{ fontWeight: 600 }}>{ad || '—'}</div>
                          {alt && <div style={{ fontSize: 11.5, color: '#888' }}>{alt}</div>}
                        </TableCell>
                        <TableCell>{it.SerialNo || '—'}</TableCell>
                        <TableCell>{it.GivenCount || '—'}</TableCell>
                        <TableCell>{kalib ? (Number(it.CalibrationStatu) === 1 ? (it.CalibrationDate || '—') : 'Yok') : '—'}</TableCell>
                        <TableCell sx={{ maxWidth: 220, whiteSpace: 'normal', color: it.Aciklama ? 'text.primary' : 'text.disabled' }}>
                          {it.Aciklama || '—'}
                        </TableCell>
                        <TableCell>
                          {it.isNew
                            ? <Chip size="small" color="secondary" label="yeni" sx={{ height: 20 }} />
                            : <Chip size="small" sx={{ ...styles.statusAlindi, height: 20 }} label="katalog" />}
                        </TableCell>
                        <TableCell padding="none" align="center" style={{ whiteSpace: 'nowrap' }}>
                          <Tooltip title="Aynı ekipmanı yeni seri no ile ekle (bilgileri kopyala)">
                            <IconButton size="small" color="secondary" onClick={() => bulkCloneToDraft(it)}><AddIcon fontSize="small" /></IconButton>
                          </Tooltip>
                          <Tooltip title="Listeden çıkar">
                            <IconButton size="small" onClick={() => bulkRemoveRow(it.uid)}><DeleteIcon fontSize="small" /></IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {bulkResult && bulkResult.Errors && bulkResult.Errors.length > 0 && (
            <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1 }}>
              Hatalı satırlar: {bulkResult.Errors.map(er => `#${er.Row} (${er.Message})`).join(', ')}
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 2.5, py: 1.5, gap: 1, backgroundColor: '#eef2f6' }}>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', mr: 'auto' }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>Toplam {bulkList.length}</Typography>
            {bulkCatalogCount > 0 && <Chip size="small" sx={styles.statusAlindi} label={`${bulkCatalogCount} katalogtan`} />}
            {bulkNewCount > 0 && <Chip size="small" color="secondary" label={`${bulkNewCount} yeni`} />}
          </Box>
          <Button onClick={() => setBulkOpen(false)} startIcon={<CloseIcon />}>Kapat</Button>
          <Button variant="contained" color="secondary" startIcon={<CheckCircleIcon />}
            disabled={bulkList.length === 0} onClick={doBulkSave} sx={{ fontWeight: 700 }}>
            Kaydet ({bulkList.length})
          </Button>
        </DialogActions>
      </Dialog>
    </Page>
  );
}

export default EkipmanYonetimi;
